import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Target, 
  Calendar, 
  Users, 
  Trophy, 
  Clock, 
  DollarSign, 
  Play, 
  Pause, 
  Plus,
  Edit,
  Eye,
  Star,
  Filter,
  Search,
  Trash2,
  X
} from 'lucide-react';
import { buildApiUrl } from '../config';
import { calculateSuspiciousWeight, isSuspiciousAccount } from '../config/suspiciousAccounts';

interface BoomEvent {
  id: number;
  name: string;
  description: string;
  start_date: string;
  end_date: string;
  prize_pool: string;
  participation_fee: string;
  participants_count: number;
  max_participants: number;
  rounds: number;
  status: 'pending' | 'upcoming' | 'active' | 'ended';
  created_by: string;
  created_at: string;
  phase: number;
  rules?: any;
  prize_distribution?: { distribution: PrizeTier[] };
}

interface PrizeTier {
  rank: number | string;
  count: number;
  prize: number;
  label?: string;
}

type PrizeDistributionState = PrizeTier[];

interface Participant {
  id: number;
  wallet_address: string;
  nickname: string;
  referral_code?: string | null;
  referred_by?: string | null;  // 추천인 코드 (추가!)
  joined_at: string;
  tokens_used: number;
  manual_rank?: number | null;
  status: 'active' | 'on_hold' | 'winner' | 'eliminated' | 'removed';
  tier?: string | null;
  reward_received?: number | null;
  payout_status?: string | null;
  display_order: number;
}

interface Winner {
  id: number;
  event_id: number;
  participant_id: number;
  wallet_address: string;
  nickname: string;
  referral_code: string;
  rank: number;
  prize_amount: number | string;
  payout_status?: string;
  tokens_used?: number | string;
  participation_time?: string;
  tier?: string;
  announced_at?: string;
  lockup_end_date?: string;
  withdrawal_schedule?: {
    month: number;
    amount: number;
    available_date: string;
  }[];
  usage_restrictions?: {
    events_only: boolean;
    staking_allowed: boolean;
    node_subscription_allowed: boolean;
    withdrawal_after: string;
  };
}

interface EventPopup {
  id: number;
  event_id: number;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'success' | 'error';
  show_time: string;
  duration: number;
  is_active: boolean;
}

const normalizePrizeDistribution = (raw: any, prizePool = 0) => {
  const source = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.distribution)
      ? raw.distribution
      : [];

  if (!Array.isArray(source)) {
    return { distribution: [] };
  }

  const distribution: PrizeTier[] = source.map((item: any, index: number) => {
    const rank = item?.rank ?? item?.tier ?? item?.label ?? item?.name ?? `${index + 1}`;
    const label = resolveTierLabel(item, typeof rank === 'string' ? rank : `${rank}위`);
    const countRaw = item?.count ?? item?.quantity ?? item?.split;
    const count =
      countRaw !== undefined
        ? Math.max(0, Number(countRaw) || 0)
        : Math.max(0, resolveTierCount(item));
    let prize = 0;
    if (item?.prize !== undefined) {
      prize = Number(item.prize) || 0;
    } else if (item?.amount !== undefined) {
      prize = Number(item.amount) || 0;
    } else if (item?.reward !== undefined) {
      prize = Number(item.reward) || 0;
    } else if (item?.prize_amount !== undefined) {
      prize = Number(item.prize_amount) || 0;
    } else if (item?.percentage !== undefined) {
      const totalForTier = prizePool * (Number(item.percentage) || 0) / 100;
      prize = count > 0 ? totalForTier / count : totalForTier;
    }
    return {
      rank,
      count: count || 0,
      prize: prize || 0,
      label
    };
  });

  return { distribution };
};

const normalizeRules = (raw: any) => (raw && typeof raw === 'object' ? raw : {});

const resolveTierLabel = (tier: any, fallback: string) => {
  if (!tier) return fallback;
  return tier.tier || tier.label || tier.name || tier.rank_label || tier.rank || fallback;
};

const resolveTierCount = (tier: any): number => {
  if (!tier) return 0;
  if (tier.count !== undefined) return Number(tier.count) || 0;
  if (tier.quantity !== undefined) return Number(tier.quantity) || 0;
  if (tier.split !== undefined) return Number(tier.split) || 0;
  if (typeof tier.rank === 'string') {
    const rangeMatch = tier.rank.match(/(\d+)\s*-\s*(\d+)/);
    if (rangeMatch) {
      const start = parseInt(rangeMatch[1], 10);
      const end = parseInt(rangeMatch[2], 10);
      if (!Number.isNaN(start) && !Number.isNaN(end) && end >= start) {
        return end - start + 1;
      }
    }
  }
  return 1;
};

const resolveTierRankStart = (tier: any, fallback: number) => {
  if (!tier) return fallback;
  if (tier.rank_start !== undefined) {
    const value = Number(tier.rank_start);
    if (!Number.isNaN(value)) return value;
  }
  if (typeof tier.rank === 'number') return tier.rank;
  if (typeof tier.rank === 'string') {
    const rangeMatch = tier.rank.match(/(\d+)\s*-\s*(\d+)/);
    if (rangeMatch) {
      const start = parseInt(rangeMatch[1], 10);
      if (!Number.isNaN(start)) {
        return start;
      }
    }
    const singleMatch = tier.rank.match(/(\d+)/);
    if (singleMatch) {
      const start = parseInt(singleMatch[1], 10);
      if (!Number.isNaN(start)) {
        return start;
      }
    }
  }
  return fallback;
};

const resolveTierPrize = (tier: any, prizePool: number): number => {
  if (!tier) return 0;
  if (tier.prize !== undefined) return Number(tier.prize) || 0;
  if (tier.amount !== undefined) return Number(tier.amount) || 0;
  if (tier.percentage !== undefined) {
    const total = prizePool * (Number(tier.percentage) || 0) / 100;
    const count = resolveTierCount(tier);
    if (count > 0) {
      return total / count;
    }
    return total;
  }
  return 0;
};

const buildTierRanges = (distribution: PrizeTier[], prizePool: number) => {
  const ranges: Array<{ tier: any; label: string; prize: number; count: number; start: number; end: number }> = [];
  let nextRank = 1;

  distribution.forEach((tier) => {
    const count = Number(tier?.count ?? resolveTierCount(tier)) || 0;
    if (count <= 0) {
      return;
    }
    let start = typeof tier.rank === 'number'
      ? tier.rank
      : resolveTierRankStart(tier, nextRank);
    if (start < nextRank) {
      start = nextRank;
    }
    const prize = Number(tier?.prize ?? resolveTierPrize(tier, prizePool)) || 0;
    const label = tier?.label || resolveTierLabel(tier, `${start}위`);
    const end = start + count - 1;
    ranges.push({
      tier,
      label,
      prize,
      count,
      start,
      end
    });
    nextRank = end + 1;
  });

  return ranges;
};

const findTierInfoForRank = (rank: number, distribution: any[], prizePool: number) => {
  if (!Array.isArray(distribution) || !Number.isFinite(rank)) return null;
  const ranges = buildTierRanges(distribution, prizePool);
  return ranges.find((range) => rank >= range.start && rank <= range.end) || null;
};

// 이벤트 ID에 따른 기본 상금 분배 (1차: id=1, 2차: id=2)
const getDefaultPrizeDistribution = (eventId?: number): PrizeDistributionState => {
  // 2차 이벤트 (id=2)
  if (eventId === 2) {
    return [
      { rank: '1등', count: 1, prize: 80000 },   // 1명 × 80,000 = 80,000 VTTN (44.4%)
      { rank: '2등', count: 5, prize: 6000 },   // 5명 × 6,000 = 30,000 VTTN (16.7%)
      { rank: '3등', count: 30, prize: 1000 },  // 30명 × 1,000 = 30,000 VTTN (16.7%)
      { rank: '4등', count: 100, prize: 200 },  // 100명 × 200 = 20,000 VTTN (11.1%)
      { rank: '5등', count: 200, prize: 100 }   // 200명 × 100 = 20,000 VTTN (11.1%)
    ];
  }
  // 1차 이벤트 (기본값)
  return [
    { rank: '1등', count: 1, prize: 75000 },
    { rank: '2등', count: 4, prize: 5000 },
    { rank: '3등', count: 25, prize: 200 },
    { rank: '4등', count: 100, prize: 100 },
    { rank: '5등', count: 200, prize: 50 }
  ];
};

const DEFAULT_PRIZE_DISTRIBUTION: PrizeDistributionState = getDefaultPrizeDistribution(1); // 기본값은 1차

const formatTierLabel = (rank?: number | null, eventId?: number) => {
  if (!rank || rank <= 0) return '-';
  
  // 2차 이벤트 (id=2): 1등(1명), 2등(5명), 3등(30명), 4등(100명), 5등(200명)
  if (eventId === 2) {
    if (rank === 1) return '1등';                    // rank 1
    if (rank >= 2 && rank <= 6) return '2등';       // rank 2-6 (5명)
    if (rank >= 7 && rank <= 36) return '3등';      // rank 7-36 (30명)
    if (rank >= 37 && rank <= 136) return '4등';   // rank 37-136 (100명)
    if (rank >= 137 && rank <= 336) return '5등';   // rank 137-336 (200명)
    return '5등'; // 기본값
  }
  
  // 1차 이벤트 (기본값): 1등(1명), 2등(4명), 3등(25명), 4등(100명), 5등(200명)
  if (rank === 1) return '1등';
  if (rank >= 2 && rank <= 5) return '2등';          // rank 2-5 (4명)
  if (rank >= 6 && rank <= 30) return '3등';        // rank 6-30 (25명)
  if (rank >= 31 && rank <= 130) return '4등';      // rank 31-130 (100명)
  if (rank >= 131 && rank <= 330) return '5등';     // rank 131-330 (200명)
  return '5등'; // 기본값
};

// 🚨 상위 등수 보호 로직에 사용할 의심 추천 코드 목록
const GUARDED_TOP3_REFERRAL_CODES = new Set([
  'FLN4AQ', // yss6012
  '1NQLZ8', // jtk0770
  '4VLJR8', // les4243
  'RC2D03', // kjh9246
  'S9SOEZ', // Kang7243
  'QLX2EK', // ys3364
  '9AZP36',
  'GB6MMO',
  'PO8AXP',
  'PFHPFE',
  'U1S8VN',
  'V1HLW4',
  'A88888',
  'UK1YML'
]);

const TOP3_PROTECTED_RANK = 3;

const toLocalDateTimeInput = (isoString?: string | null) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const toIsoString = (localValue?: string | null) => {
  if (!localValue) return undefined;
  const date = new Date(localValue);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }
  return date.toISOString();
};

interface ParticipantChange {
  manualRank?: number | null;
  status?: Participant['status'];
  referralCode?: string | null;
  rewardReceived?: number | null;
}

const PARTICIPANT_STATUS_OPTIONS: Array<{
  key: Participant['status'];
  label: string;
  color: string;
}> = [
  { key: 'active', label: '참여', color: 'bg-blue-500' },
  { key: 'winner', label: '당첨', color: 'bg-orange-500' },
  { key: 'on_hold', label: '보류', color: 'bg-purple-500' },
  { key: 'eliminated', label: '탈락', color: 'bg-gray-600' }
];

const formatBoomEventDetail = (detail: any): BoomEvent => {
  const startRaw =
    detail?.start_time ||
    detail?.startTime ||
    detail?.startDate ||
    detail?.start ||
    null;
  const endRaw =
    detail?.end_time ||
    detail?.endTime ||
    detail?.endDate ||
    detail?.end ||
    null;

  return {
    id: detail?.id ?? 0,
    name: detail?.title || detail?.eventType || `Boom Event #${detail?.id ?? ''}`,
    description: detail?.description || '',
    start_date: startRaw ? new Date(startRaw).toISOString() : new Date().toISOString(),
    end_date: endRaw ? new Date(endRaw).toISOString() : new Date().toISOString(),
    prize_pool: (detail?.rewardPool ?? detail?.reward_amount ?? 0).toString(),
    participation_fee: (detail?.entryFee ?? detail?.required_amount ?? 0).toString(),
    participants_count: detail?.currentParticipants ?? 0,
    max_participants: detail?.maxParticipants ?? 0,
    rounds: detail?.rounds ?? 0,
    status: (detail?.status as BoomEvent['status']) || 'upcoming',
    created_by: 'system',
    created_at: startRaw ? new Date(startRaw).toISOString() : new Date().toISOString(),
    phase: detail?.phase ?? 1,
    prize_distribution: normalizePrizeDistribution(
      detail?.prizeDistribution ?? detail?.prize_distribution,
      Number(detail?.rewardPool ?? detail?.reward_amount ?? 0)
    ),
    rules: normalizeRules(detail?.rules)
  };
};

const fetchBoomEventDetail = async (eventId: number) => {
  try {
    const response = await fetch(buildApiUrl(`/api/admin/boom-events/${eventId}`), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      console.error('Boom 이벤트 상세 조회 실패:', response.status, response.statusText);
      return null;
    }

    const data = await response.json();
    if (!data?.success) {
      console.warn('Boom 이벤트 상세 응답이 success=false 입니다.', data);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Boom 이벤트 상세 API 호출 오류:', error);
    return null;
  }
};

const BoomEventsPage: React.FC = () => {
  const [events, setEvents] = useState<BoomEvent[]>([]);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [participantsTotal, setParticipantsTotal] = useState(0);
  const [participantChanges, setParticipantChanges] = useState<Record<number, ParticipantChange>>({});
  const [winners, setWinners] = useState<Winner[]>([]);
  const [eventPopups, setEventPopups] = useState<EventPopup[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<BoomEvent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('events');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [showWinnersModal, setShowWinnersModal] = useState(false);
  const [showPopupModal, setShowPopupModal] = useState(false);
  const [showParticipationCompleteModal, setShowParticipationCompleteModal] = useState(false);
  
  // 🆕 통합 이벤트 관리 팝업
  const [showEventManagementModal, setShowEventManagementModal] = useState(false);
  const [managementTab, setManagementTab] = useState<'info' | 'participants' | 'winners' | 'payment' | 'stats'>('info');
  const [winnerSearchTerm, setWinnerSearchTerm] = useState('');
  const [participantTabSearch, setParticipantTabSearch] = useState('');
  const [participantModalSearch, setParticipantModalSearch] = useState('');

  const matchesWinnerSearch = useCallback((winner: Participant) => {
    const term = winnerSearchTerm.trim().toLowerCase();
    if (!term) return true;
    return (
      (winner.nickname || '').toLowerCase().includes(term) ||
      (winner.referral_code || '').toLowerCase().includes(term) ||
      (winner.wallet_address || '').toLowerCase().includes(term)
    );
  }, [winnerSearchTerm]);

  const filteredWinnersList = useMemo(() => {
    const term = winnerSearchTerm.trim().toLowerCase();
    if (!term) return winners;
    return winners.filter((winner) => {
      const nickname = (winner.nickname || '').toLowerCase();
      const referral = (winner.referral_code || '').toLowerCase();
      const wallet = (winner.wallet_address || '').toLowerCase();
      return nickname.includes(term) || referral.includes(term) || wallet.includes(term);
    });
  }, [winners, winnerSearchTerm]);
  
  // 당첨자 검색 팝업
  const [showWinnerSearchModal, setShowWinnerSearchModal] = useState(false);
  const [winnerSearchQuery, setWinnerSearchQuery] = useState('');
  const [winnerSearchResults, setWinnerSearchResults] = useState<Participant[]>([]);
  const [selectedRankForAdd, setSelectedRankForAdd] = useState<number | null>(null);
  const [editingWinnerId, setEditingWinnerId] = useState<number | null>(null);
  const [editingEvent, setEditingEvent] = useState<BoomEvent | null>(null);
  const [countdowns, setCountdowns] = useState<{[key: number]: string}>({});
  const [realTimeParticipants, setRealTimeParticipants] = useState<{[key: number]: number}>({});
  const [newPopup, setNewPopup] = useState({
    title: '',
    message: '',
    type: 'info' as 'info' | 'warning' | 'success' | 'error',
    duration: 5000
  });
  const [newEvent, setNewEvent] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    prize_pool: '',
    participation_fee: '',
    max_participants: '',
    rounds: '',
    phase: '1'
  });
  const [prizeDistribution, setPrizeDistribution] = useState<PrizeDistributionState>(
    DEFAULT_PRIZE_DISTRIBUTION.map((tier) => ({ ...tier }))
  );

  const totalPrizeAmount = useMemo(
    () =>
      prizeDistribution.reduce(
        (sum, tier) => sum + (Number(tier.prize) || 0) * (Number(tier.count) || 0),
        0
      ),
    [prizeDistribution]
  );

  const filteredParticipants = useMemo(() => {
    const term = participantTabSearch.trim().toLowerCase();
    if (!term) return participants;
    return participants.filter((participant) => {
      const nickname = (participant.nickname || '').toLowerCase();
      const referral = (participant.referral_code || '').toLowerCase();
      const wallet = (participant.wallet_address || '').toLowerCase();
      return nickname.includes(term) || referral.includes(term) || wallet.includes(term);
    });
  }, [participants, participantTabSearch]);

  const filteredParticipantsForModal = useMemo(() => {
    const term = participantModalSearch.trim().toLowerCase();
    if (!term) return participants;
    return participants.filter((participant) => {
      const nickname = (participant.nickname || '').toLowerCase();
      const referral = (participant.referral_code || '').toLowerCase();
      const wallet = (participant.wallet_address || '').toLowerCase();
      return (
        nickname.includes(term) ||
        referral.includes(term) ||
        wallet.includes(term)
      );
    });
  }, [participants, participantModalSearch]);

  const handlePrizeTierChange = (index: number, field: 'rank' | 'count' | 'prize', value: string) => {
    setPrizeDistribution((prev) => {
      const next = [...prev];
      const current = { ...next[index] };
      if (field === 'rank') {
        current.rank = value;
      } else if (field === 'count') {
        const parsed = Number(value);
        current.count = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
      } else if (field === 'prize') {
        const parsed = Number(value);
        current.prize = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
      }
      next[index] = current;
      return next;
    });
  };

  const addPrizeTier = () => {
    setPrizeDistribution((prev) => [
      ...prev,
      { rank: `${prev.length + 1}위`, count: 1, prize: 0 }
    ]);
  };

  const removePrizeTier = (index: number) => {
    setPrizeDistribution((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    // 🟢 편집 모드일 때는 Prize Pool을 자동 업데이트하지 않음 (기존 값 유지)
    if (editingEvent) return;
    
    setNewEvent((prev) => {
      const computedPrize = totalPrizeAmount ? totalPrizeAmount.toString() : '0';
      if (prev.prize_pool === computedPrize) {
        return prev;
      }
      return { ...prev, prize_pool: computedPrize };
    });
  }, [totalPrizeAmount, editingEvent]);

  const computeTierRangesForEvent = (event: BoomEvent | null) => {
    if (!event) return [];
    const prizePoolAmount = Number(event.prize_pool || 0);
    const distribution = normalizePrizeDistribution(
      event.prize_distribution,
      prizePoolAmount
    ).distribution;
    return buildTierRanges(distribution, prizePoolAmount);
  };

  const sortParticipants = (items: Participant[]) =>
    [...items]
      .sort(
        (a, b) =>
          new Date(b.joined_at).getTime() - new Date(a.joined_at).getTime()
      )
      .map((item, index) => ({
        ...item,
        display_order: index + 1
      }));

  const setParticipantsWithOrdering = (items: Participant[]) => {
    setParticipants(sortParticipants(items));
  };

  const findParticipantById = (participantId: number) =>
    participants.find((participant) => participant.id === participantId);

  const getRewardForRank = (rank: number | null | undefined) => {
    if (!selectedEvent || rank === null || rank === undefined) return null;
    const prizePoolAmount = Number(selectedEvent.prize_pool || 0);
    const distribution = normalizePrizeDistribution(
      selectedEvent.prize_distribution,
      prizePoolAmount
    ).distribution;
    const tier = findTierInfoForRank(rank, distribution, prizePoolAmount);
    return tier ? tier.prize : null;
  };

  const updateParticipantLocal = (
    participantId: number,
    updates: Partial<Participant>
  ) => {
    setParticipants((prev) => {
      const next = prev.map((participant) =>
        participant.id === participantId ? { ...participant, ...updates } : participant
      );
      return sortParticipants(next);
    });
  };

  const registerParticipantChange = (
    participantId: number,
    change: ParticipantChange
  ) => {
    setParticipantChanges((prev) => {
      const combined = { ...(prev[participantId] || {}), ...change };
      const cleaned: ParticipantChange = {};
      if (combined.manualRank !== undefined) cleaned.manualRank = combined.manualRank;
      if (combined.status !== undefined) cleaned.status = combined.status;
      if (combined.referralCode !== undefined) cleaned.referralCode = combined.referralCode;
      if (combined.rewardReceived !== undefined) cleaned.rewardReceived = combined.rewardReceived;

      if (Object.keys(cleaned).length === 0) {
        if (participantId in prev) {
          const next = { ...prev };
          delete next[participantId];
          return next;
        }
        return prev;
      }

      return {
        ...prev,
        [participantId]: cleaned
      };
    });
  };

  const clearParticipantChange = (participantId: number) => {
    setParticipantChanges((prev) => {
      if (!(participantId in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[participantId];
      return next;
    });
  };

  useEffect(() => {
    loadEvents();
  }, []);
  
  // 이벤트 관리 모달이 열릴 때 당첨자 로드
  useEffect(() => {
    if (showEventManagementModal && selectedEvent) {
      loadWinners(selectedEvent.id);
    }
  }, [showEventManagementModal, selectedEvent]);
  
  // selectedEvent가 변경될 때마다 당첨자 로드 (탭 전환 시에도)
  useEffect(() => {
    if (selectedEvent) {
      loadWinners(selectedEvent.id);
    }
  }, [selectedEvent?.id]);
  
  useEffect(() => {
    if (events.length > 0) {
      loadRealTimeData();
      
      // 실시간 데이터 업데이트 (30초마다)
      const interval = setInterval(() => {
        loadRealTimeData();
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [events]);

  // 카운트다운 업데이트
  useEffect(() => {
    const updateCountdowns = () => {
      const newCountdowns: {[key: number]: string} = {};
      events.forEach(event => {
        const now = new Date().getTime();
        const startTime = new Date(event.start_date).getTime();
        const endTime = new Date(event.end_date).getTime();
        
        let targetTime = startTime;
        let prefix = '시작까지: ';
        
        if (event.status === 'active') {
          targetTime = endTime;
          prefix = '종료까지: ';
        } else if (event.status === 'ended') {
          newCountdowns[event.id] = '이벤트 종료';
          return;
        }
        
        const distance = targetTime - now;
        
        if (distance > 0) {
          const days = Math.floor(distance / (1000 * 60 * 60 * 24));
          const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((distance % (1000 * 60)) / 1000);
          
          newCountdowns[event.id] = `${prefix}${days}일 ${hours}시간 ${minutes}분 ${seconds}초`;
        } else {
          newCountdowns[event.id] = event.status === 'upcoming' ? '이벤트 시작!' : '이벤트 종료!';
        }
      });
      setCountdowns(newCountdowns);
    };
    
    updateCountdowns();
    const interval = setInterval(updateCountdowns, 1000);
    
    return () => clearInterval(interval);
  }, [events]);

  const loadEvents = async () => {
    try {
      const response = await fetch(buildApiUrl('/api/admin/boom-events'));

      if (!response.ok) {
        throw new Error(`Boom 이벤트 목록 호출 실패: ${response.status}`);
      }

        const data = await response.json();
      const apiEvents = data?.events ?? data?.data ?? [];

      const formattedEvents: BoomEvent[] = Array.isArray(apiEvents)
        ? apiEvents.map((event: any) => ({
            id: event.id,
            name: event.title || event.name || `${event.round_number ?? 1}차 BOOM 이벤트`,
            description: event.description || '',
            start_date:
              event.start_time ||
              event.startTime ||
              event.startDate ||
              event.start ||
              event.start_date ||
              new Date().toISOString(),
            end_date:
              event.end_time ||
              event.endTime ||
              event.endDate ||
              event.end ||
              event.end_date ||
              new Date().toISOString(),
            prize_pool: (
              event.reward_pool ??
              event.reward_amount ??
              event.rewardPool ??
              event.prizePool ??
              event.prize_pool ??
              0
            ).toString(),
            participation_fee: (event.required_amount ?? event.entryFee ?? event.participationFee ?? event.entry_fee ?? event.participation_fee ?? 0).toString(),
            participants_count: Number(event.participant_count ?? event.currentParticipants ?? event.participants_count ?? 0),
            max_participants: Number(event.max_participants ?? event.maxParticipants ?? 0),
            rounds: Number(event.rounds ?? event.round_number ?? 1),
            status: event.status || 'upcoming',
            created_by: event.created_by || 'admin',
            created_at: event.created_at || new Date().toISOString(),
            phase: Number(event.priority ?? event.phase ?? 1),
            prize_distribution: normalizePrizeDistribution(
              event.prize_distribution ?? event.prizeDistribution,
              Number(
                event.reward_pool ??
                  event.reward_amount ??
                  event.rewardPool ??
                  event.prizePool ??
                  event.prize_pool ??
                  0
              )
            ),
            rules: normalizeRules(event.rules)
          }))
        : [];
          
          setEvents(formattedEvents);
          console.log('✅ BOOM 이벤트 로드 완료:', formattedEvents.length, '개');
    } catch (error) {
      console.error('BOOM 이벤트 로딩 실패:', error);
      setEvents([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadRealTimeData = async () => {
    try {
      if (!events.length) {
        setRealTimeParticipants({});
        return;
      }

      const counts: { [key: number]: number } = {};

      await Promise.all(
        events.map(async (event) => {
          try {
            const response = await fetch(
              buildApiUrl(`/api/admin/boom-events/${event.id}/participants/realtime`)
            );
            if (!response.ok) {
              return;
            }
          const data = await response.json();
            if (data?.success && data.stats) {
              counts[event.id] = Number(data.stats.total_participants || 0);
        }
      } catch (apiError) {
            console.debug('실시간 데이터 API 호출 실패:', apiError);
      }
        })
      );

      setRealTimeParticipants(counts);
    } catch (error) {
      console.error('실시간 데이터 로딩 실패:', error);
    }
  };

  /**
   * 당첨자 목록 로딩 (boom_event_winners 테이블에서만)
   */
  const loadWinners = async (eventId: number) => {
    try {
      console.log(`[당첨자 로드] eventId: ${eventId}`);
      const response = await fetch(
        buildApiUrl(`/api/admin/boom-events/${eventId}/winners`)
      );
      
      if (!response.ok) {
        console.error('당첨자 로딩 실패:', response.status, response.statusText);
        setWinners([]);
        return;
      }

      const data = await response.json();
      console.log(`[당첨자 로드 응답] eventId: ${eventId}, winners count:`, data?.winners?.length || 0, data);
      
      const winnersList = Array.isArray(data?.winners) ? data.winners.map((w: any) => ({
        ...w,
        wallet_address: w.user_address || w.wallet_address || '',
        rank: w.rank || null,
        prize_amount: w.prize_amount || 0
      })) : [];
      
      console.log(`[당첨자 로드 완료] eventId: ${eventId}, 최종 winners count:`, winnersList.length);
      setWinners(winnersList);
    } catch (error) {
      console.error('당첨자 로딩 오류:', error);
      setWinners([]);
    }
  };

  const loadParticipants = async (eventId: number) => {
    try {
      setParticipants([]);
      
    const response = await fetch(
      buildApiUrl(`/api/admin/boom-events/${eventId}/participants?limit=all`)
    );
      if (!response.ok) {
        throw new Error(`참가자 로딩 실패: ${response.status}`);
      }

          const data = await response.json();
      const participantList = Array.isArray(data?.participants) ? data.participants : [];

      const formattedParticipants: Participant[] = participantList.map((p: any, index: number) => {
        const rawReward =
          p.reward_received ?? p.prize_amount ?? null;
        const rewardValue =
          rawReward === null || rawReward === undefined ? null : Number(rawReward);
        return {
          id: p.id || index + 1,
          wallet_address: p.user_address || p.wallet_address || '0x0000000000000000000000000000000000000000',
          nickname: p.nickname || p.user_nickname || `참여자${index + 1}`,
          referral_code: p.referral_code ?? p.user_referral_code ?? null,
          joined_at: p.participated_at || p.participation_time || new Date().toISOString(),
          tokens_used: Number(p.tokens_used ?? p.payment_amount ?? 0),
          manual_rank: p.manual_rank ?? null,
          status: (p.status as Participant['status']) || (p.is_winner ? 'winner' : 'active'),
          tier: p.tier || null,
          reward_received: rewardValue,
          payout_status: p.payout_status || null,
          display_order: index + 1
        };
      });

      setParticipantsWithOrdering(formattedParticipants);
      setParticipantsTotal(data?.pagination?.total ?? formattedParticipants.length);
      setParticipantChanges({});
      await loadWinners(eventId);
    } catch (error) {
      console.error('참가자 로딩 실패:', error);
      setParticipants([]);
      setParticipantsTotal(0);
    }
  };

  const handleParticipantRankChange = (participantId: number, value: string) => {
    if (value === '') {
      updateParticipantLocal(participantId, { manual_rank: null, reward_received: null });
      registerParticipantChange(participantId, { manualRank: null, rewardReceived: null });
      return;
    }
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return;
    }
    const reward = getRewardForRank(parsed);
    updateParticipantLocal(participantId, { manual_rank: parsed, reward_received: reward ?? null });
    registerParticipantChange(participantId, { manualRank: parsed, rewardReceived: reward ?? null });
  };

  const handleParticipantStatusChange = (participantId: number, status: Participant['status']) => {
    const currentParticipant = findParticipantById(participantId);
    const reward =
      status === 'winner'
        ? getRewardForRank(currentParticipant?.manual_rank ?? null)
        : null;
    updateParticipantLocal(participantId, {
      status,
      reward_received: reward ?? (status === 'winner' ? currentParticipant?.reward_received ?? null : null)
    });
    registerParticipantChange(participantId, {
      status,
      rewardReceived: reward ?? (status === 'winner' ? currentParticipant?.reward_received ?? null : null)
    });
  };

  const patchParticipant = async (
    participantId: number,
    payload: Record<string, unknown>,
    successMessage?: string
  ) => {
    if (!selectedEvent) return;

    try {
      const response = await fetch(
        buildApiUrl(`/api/admin/boom-events/${selectedEvent.id}/participants/${participantId}`),
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.error || `HTTP ${response.status}`);
      }

      if (successMessage) {
        alert(successMessage);
      }
      await loadParticipants(selectedEvent.id);
      clearParticipantChange(participantId);
    } catch (error) {
      console.error('참가자 정보 업데이트 실패:', error);
      alert('참가자 정보를 업데이트하지 못했습니다.');
    }
  };

const buildParticipantPayload = (change: ParticipantChange) => {
  const payload: Record<string, unknown> = {};
  if (change.manualRank !== undefined) {
    payload.manualRank = change.manualRank === null ? null : Number(change.manualRank);
  }
  if (change.status !== undefined) {
    payload.status = change.status;
  }
  if (change.referralCode !== undefined) {
    payload.referralCode = change.referralCode;
  }
  if (change.rewardReceived !== undefined) {
    payload.rewardReceived =
      change.rewardReceived === null ? null : Number(change.rewardReceived);
  }
  return payload;
};

  const handleSaveParticipant = async (
    participantId: number,
    overrideChange?: ParticipantChange,
    successMessage = '참가자 정보를 저장했습니다.'
  ) => {
    const baseChange = participantChanges[participantId] || {};
    const mergedChange: ParticipantChange = { ...baseChange, ...(overrideChange || {}) };
    const currentParticipant = findParticipantById(participantId);
    const isWinner =
      (mergedChange.status ?? currentParticipant?.status) === 'winner';
    const rankValue =
      mergedChange.manualRank !== undefined
        ? mergedChange.manualRank
        : currentParticipant?.manual_rank ?? null;
    if (isWinner && rankValue !== null && mergedChange.rewardReceived === undefined) {
      mergedChange.rewardReceived = getRewardForRank(rankValue);
    }
    if (!isWinner && mergedChange.rewardReceived === undefined && currentParticipant?.reward_received !== undefined) {
      mergedChange.rewardReceived = null;
    }
    const payload = buildParticipantPayload(mergedChange);

    if (Object.keys(payload).length === 0) {
      alert('저장할 변경 사항이 없습니다.');
      return;
    }

    await patchParticipant(participantId, payload, successMessage);
  };

  const handleQuickHold = (participantId: number) => {
    updateParticipantLocal(participantId, { status: 'on_hold', reward_received: null });
    registerParticipantChange(participantId, { status: 'on_hold', rewardReceived: null });
    handleSaveParticipant(
      participantId,
      { status: 'on_hold', rewardReceived: null },
      '참여자를 보류 처리했습니다.'
    );
  };

  /**
   * 전체 당첨자 저장 (데이터베이스에 확정 저장)
   */
  const handleBulkSave = async () => {
    if (!selectedEvent) return;
    
    // ✅ 전체 참가자에서 당첨자 필터링 (페이지네이션 무관)
    console.log(`📊 handleBulkSave 시작: participants.length = ${participants.length}`);
    
    const winners = participants.filter(p => {
      const isWinner = p.status === 'winner' && p.manual_rank !== null && p.manual_rank !== undefined;
      if (isWinner) {
        console.log(`✅ 당첨자 발견: ${p.nickname} (rank: ${p.manual_rank})`);
      }
      return isWinner;
    });
    
    console.log(`📊 필터링된 당첨자: ${winners.length}명`);
    
    if (winners.length === 0) {
      alert('⚠️ 저장할 당첨자가 없습니다!\n\n현재 로드된 참가자: ' + participants.length + '명\n\n"전체 당첨자 선정 (랜덤)" 버튼을 먼저 눌러주세요.');
      return;
    }
    
    if (winners.length < 330) {
      alert(`⚠️ 당첨자가 ${winners.length}명만 발견되었습니다!\n\n예상: 330명\n실제: ${winners.length}명\n\n"전체 참가자" 탭에서 전체 로드 후 다시 시도하세요.`);
      return;
    }

    if (!window.confirm(`💾 당첨자 ${winners.length}명을 데이터베이스에 저장하시겠습니까?\n\n• 저장 후에는 브라우저 새로고침해도 유지됩니다.\n• 기존 저장된 당첨자는 삭제되고 새로 저장됩니다.`)) {
      return;
    }

    try {
      // 1단계: 데이터베이스 초기화 (기존 당첨자 삭제)
      const resetResponse = await fetch(
        buildApiUrl(`/api/admin/boom-events/${selectedEvent.id}/winners/reset`),
        {
          method: 'DELETE',
        headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!resetResponse.ok) {
        const data = await resetResponse.json().catch(() => ({}));
        throw new Error(`초기화 실패: ${data.error || resetResponse.statusText}`);
      }

      // 2단계: participations 테이블 업데이트
      const participationUpdates = winners.map(w => ({
        id: w.id,
        status: 'winner',
        manualRank: w.manual_rank,
        rewardReceived: null // 아직 지급 안 함
      }));

      const updateResponse = await fetch(
        buildApiUrl(`/api/admin/boom-events/${selectedEvent.id}/participants`),
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ participants: participationUpdates })
        }
      );

      if (!updateResponse.ok) {
        const data = await updateResponse.json().catch(() => ({}));
        throw new Error(`참가자 업데이트 실패: ${data.error || updateResponse.statusText}`);
      }

      // 3단계: winners 테이블에 저장
      const prizePoolAmount = Number(selectedEvent.prize_pool || 0);
      const distribution = normalizePrizeDistribution(
        selectedEvent.prize_distribution,
        prizePoolAmount
      ).distribution;
      const tierRanges = buildTierRanges(distribution, prizePoolAmount);

      const winnersData = winners.map(w => {
        const range = tierRanges.find(r => w.manual_rank! >= r.start && w.manual_rank! <= r.end);
        
        // ✅ manual_rank를 실제 등수(tier)로 변환
        let actualRank = 5; // 기본값
        if (w.manual_rank === 1) actualRank = 1;
        else if (w.manual_rank! >= 2 && w.manual_rank! <= 5) actualRank = 2;
        else if (w.manual_rank! >= 6 && w.manual_rank! <= 30) actualRank = 3;
        else if (w.manual_rank! >= 31 && w.manual_rank! <= 130) actualRank = 4;
        else if (w.manual_rank! >= 131 && w.manual_rank! <= 330) actualRank = 5;
        
        return {
          participant_id: w.id,
          user_address: w.wallet_address,
          rank: actualRank,  // ✅ 1~5등으로 저장
          prize_amount: range?.prize || 0
        };
      });

      const winnersResponse = await fetch(
        buildApiUrl(`/api/admin/boom-events/${selectedEvent.id}/winners/bulk-save`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ winners: winnersData })
        }
      );

      if (!winnersResponse.ok) {
        const data = await winnersResponse.json().catch(() => ({}));
        throw new Error(`당첨자 저장 실패: ${data.error || winnersResponse.statusText}`);
      }

      alert(`✅ 당첨자 ${winners.length}명이 데이터베이스에 저장되었습니다!`);
      setParticipantChanges({});
      
      // 저장 후 당첨자 목록과 참가자 목록 모두 새로고침
      await Promise.all([
        loadWinners(selectedEvent.id),
        loadParticipants(selectedEvent.id)
      ]);
    } catch (error) {
      console.error('당첨자 저장 실패:', error);
      alert(`저장 실패: ${error instanceof Error ? error.message : '알 수 없는 오류'}`);
    }
  };

  const renderPrizeDistributionEditor = () => (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-gray-600">
          총 상금:
          <span className="font-semibold text-orange-600 ml-1">
            {totalPrizeAmount.toLocaleString()} VTTN
          </span>
        </div>
        <div className="text-xs text-gray-500">
          등수별 상금 = `인원 × 개별 상금`, 비율은 자동 계산됩니다.
        </div>
      </div>
      <div className="space-y-3">
        {prizeDistribution.map((tier, index) => {
          const totalForTier = (Number(tier.count) || 0) * (Number(tier.prize) || 0);
          const percentage =
            totalPrizeAmount > 0 ? (totalForTier / totalPrizeAmount) * 100 : 0;
          return (
            <div
              key={index}
              className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end bg-gray-50 p-3 rounded-lg border border-gray-200"
            >
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">등수</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  value={tier.rank}
                  onChange={(e) => handlePrizeTierChange(index, 'rank', e.target.value)}
                  placeholder="예: 1등 또는 1-5위"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">인원</label>
                <input
                  type="number"
                  min={0}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm text-right"
                  value={tier.count}
                  onChange={(e) => handlePrizeTierChange(index, 'count', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  개별 상금 (VTTN)
                </label>
                <input
                  type="number"
                  min={0}
                  step="1"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm text-right"
                  value={tier.prize}
                  onChange={(e) => handlePrizeTierChange(index, 'prize', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">총 상금</label>
                <div className="border border-gray-200 rounded px-2 py-1 text-sm bg-white text-right">
                  {totalForTier.toLocaleString()} VTTN
                </div>
                <div className="text-xs text-gray-500 text-right mt-1">
                  비율 {percentage.toFixed(2)}%
                </div>
              </div>
              <div className="flex justify-end md:justify-center">
                <button
                  type="button"
                  className="px-3 py-1 text-xs text-red-600 border border-red-200 rounded hover:bg-red-50"
                  onClick={() => removePrizeTier(index)}
                  disabled={prizeDistribution.length <= 1}
                >
                  삭제
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <button
        type="button"
        className="w-full border border-dashed border-gray-400 text-gray-600 rounded py-2 text-sm hover:bg-gray-100"
        onClick={addPrizeTier}
      >
        + 새 당첨 등수 추가
      </button>
    </div>
  );

  /**
   * 🎯 가중치 계산 함수 (별도 파일에서 import)
   */
  const calculateWeight = (participant: Participant): number => {
    return calculateSuspiciousWeight(participant.referred_by, participant.referral_code);
  };

  /**
   * 🎯 가중치 기반 랜덤 선택
   */
  const weightedRandomSelection = (
    pool: Participant[], 
    count: number
  ): Participant[] => {
    const selected: Participant[] = [];
    const remaining = [...pool];
    
    for (let i = 0; i < count && remaining.length > 0; i++) {
      // 각 참가자의 가중치 계산
      const weights = remaining.map(p => calculateWeight(p));
      const totalWeight = weights.reduce((sum, w) => sum + w, 0);
      
      if (totalWeight === 0) {
        // 모든 가중치가 0이면 랜덤 선택
        const randomIndex = Math.floor(Math.random() * remaining.length);
        selected.push(remaining[randomIndex]);
        remaining.splice(randomIndex, 1);
        continue;
      }
      
      // 가중치 기반 랜덤 선택
      let random = Math.random() * totalWeight;
      let selectedIndex = 0;
      
      for (let j = 0; j < weights.length; j++) {
        random -= weights[j];
        if (random <= 0) {
          selectedIndex = j;
          break;
        }
      }
      
      // 선택된 참가자 추가 및 제거
      selected.push(remaining[selectedIndex]);
      remaining.splice(selectedIndex, 1);
    }
    
    return selected;
  };

  /**
   * 🎯 의심 추천코드 체크
   */
  /**
   * 의심 계정 여부 확인 (별도 파일에서 import)
   */
  const isSuspiciousReferrer = (referredBy: string | null | undefined): boolean => {
    return isSuspiciousAccount(referredBy);
  };

  const handleRandomAssignWinners = async () => {
    if (!selectedEvent) {
      alert('이벤트를 먼저 선택하세요.');
      return;
    }
    
    // 🔥 중요: 전체 참가자를 먼저 로드!
    if (participants.length < selectedEvent.participants_count) {
      alert(`⚠️ 전체 참가자를 먼저 로드해야 합니다!\n\n현재 로드: ${participants.length}명\n전체 참가자: ${selectedEvent.participants_count}명\n\n"전체 참가자" 탭으로 이동해서 전체 로드 후 다시 시도하세요.`);
      return;
    }
    
    if (!window.confirm('🎲 랜덤 배정을 시작하시겠습니까?\n\n• 1~3등: 의심 계정 완전 배제 (정상 계정만)\n• 4~5등: 전체 참가자 대상 가중치 랜덤\n• 프론트엔드에 임시로 당첨자가 배정됩니다.\n• "전체 당첨자 저장" 버튼을 눌러야 데이터베이스에 저장됩니다!')) {
      return;
    }

    const prizePoolAmount = Number(selectedEvent.prize_pool || 0);
    const distribution = normalizePrizeDistribution(
      selectedEvent.prize_distribution,
      prizePoolAmount
    ).distribution;
    if (!Array.isArray(distribution) || distribution.length === 0) {
      alert('이벤트에 설정된 상금 분배 정보가 없습니다.');
      return;
    }
    if (participants.length === 0) {
      alert('참여자가 없습니다.');
      return;
    }

    // 🟢 1단계: 모든 참여자를 'active' 상태로 초기화
    const resetParticipants = participants.map((participant) => ({
      ...participant,
      status: 'active' as const,
      manual_rank: null,
      reward_received: null
    }));

    // 🟢 2단계: 'active' 상태인 참여자만 선택
    const eligibleParticipants = resetParticipants.filter(
      (participant) => participant.status === 'active'
    );

    if (eligibleParticipants.length === 0) {
      alert('랜덤 선정이 가능한 참여자가 없습니다.');
      return;
    }

    // 🟢 3단계: 참가자 분류
    // 정상 계정: 의심 추천인이 아니고 자기추천도 아닌 계정
    const normalParticipants = eligibleParticipants.filter(p => 
      !isSuspiciousReferrer(p.referred_by) &&  // ✅ referred_by (추천인) 체크!
      p.referred_by !== p.referral_code        // 자기추천 체크
    );
    
    console.log(`📊 참가자 분류:
      - 전체: ${eligibleParticipants.length}명
      - 정상 계정: ${normalParticipants.length}명
      - 의심 계정: ${eligibleParticipants.length - normalParticipants.length}명
    `);

    const tierRanges = buildTierRanges(distribution, prizePoolAmount);

    if (tierRanges.length === 0) {
      alert('상금 분배 구성을 해석할 수 없습니다.');
      return;
    }

    const updatedParticipants = [...resetParticipants] as Participant[];
    const assignedIds = new Set<number>();
    
    let normalStats = { rank1_3: 0, rank4_5: 0 };
    let suspiciousStats = { rank1_3: 0, rank4_5: 0 };
    
    let currentSequentialRank = 1; // ✅ 순차 번호 (1, 2, 3, 4, 5... 330)

    // 🟢 4단계: 등수별 당첨자 선정
    // ⭐ 2차 이벤트(id=2)의 경우: 1등~4등까지 먼저 다 채우고, 모자른 인원은 5등에서 제외
    const is2ndEvent = selectedEvent.id === 2;
    let totalAssignedBefore5th = 0; // 5등 이전에 배정된 총 인원
    
    tierRanges.forEach((range, index) => {
      const assignedRank = range.start;
      const isTopRank = assignedRank >= 1 && assignedRank <= 3;
      const is4thRank = assignedRank === 4;
      const is5thRank = assignedRank === 5;
      
      let selectedParticipants: Participant[] = [];
      let actualCount = range.count; // 실제 선정할 인원수
      
      // ⭐ 2차 이벤트의 5등: 1등~4등까지 다 채운 후 남은 인원만 선정
      if (is2ndEvent && is5thRank) {
        // 1등~4등까지 필요한 총 인원: 1 + 5 + 30 + 100 = 136명
        const requiredBefore5th = 1 + 5 + 30 + 100;
        // 실제로 1등~4등까지 배정된 인원
        const remainingFor5th = eligibleParticipants.length - totalAssignedBefore5th;
        // 5등은 최대 200명이지만, 실제로는 남은 인원만큼만 선정
        actualCount = Math.min(range.count, remainingFor5th);
        console.log(`📊 5등 선정: 계획 ${range.count}명, 실제 가능 ${remainingFor5th}명, 선정 ${actualCount}명`);
      }
      
      if (isTopRank) {
        // 🔴 1~3등: 정상 계정만 (의심 계정 완전 배제)
        const availableNormal = normalParticipants.filter(p => !assignedIds.has(p.id));
        
        if (availableNormal.length < actualCount) {
          // 정상 계정이 부족하면 전체 참가자에서 선택
          const availableAll = eligibleParticipants.filter(p => !assignedIds.has(p.id));
          const shuffled = [...availableAll].sort(() => Math.random() - 0.5);
          selectedParticipants = shuffled.slice(0, actualCount);
          console.warn(`⚠️ ${assignedRank}등: 정상 계정 부족 (${availableNormal.length}/${actualCount}), 전체에서 선택`);
        } else {
          // 정상 계정에서 랜덤 선택
          const shuffled = [...availableNormal].sort(() => Math.random() - 0.5);
          selectedParticipants = shuffled.slice(0, actualCount);
          console.log(`✅ ${assignedRank}등: 정상 계정에서 ${actualCount}명 선정`);
        }
        
        normalStats.rank1_3 += selectedParticipants.filter(p => !isSuspiciousReferrer(p.referred_by)).length;
        suspiciousStats.rank1_3 += selectedParticipants.filter(p => isSuspiciousReferrer(p.referred_by)).length;
      } else if (is4thRank) {
        // 🟡 4등: 전체 참가자 대상 가중치 랜덤 (2차 이벤트의 경우 반드시 100명 채우기)
        const availableAll = eligibleParticipants.filter(p => !assignedIds.has(p.id));
        if (availableAll.length < actualCount) {
          console.warn(`⚠️ 4등: 선정 가능한 참가자 부족 (${availableAll.length}/${actualCount})`);
          selectedParticipants = availableAll; // 가능한 만큼만 선정
        } else {
          selectedParticipants = weightedRandomSelection(availableAll, actualCount);
        }
        console.log(`✅ ${assignedRank}등: 가중치 랜덤으로 ${selectedParticipants.length}명 선정`);
        
        normalStats.rank4_5 += selectedParticipants.filter(p => !isSuspiciousReferrer(p.referred_by)).length;
        suspiciousStats.rank4_5 += selectedParticipants.filter(p => isSuspiciousReferrer(p.referred_by)).length;
      } else if (is5thRank) {
        // 🟡 5등: 전체 참가자 대상 가중치 랜덤 (2차 이벤트의 경우 남은 인원만큼만)
        const availableAll = eligibleParticipants.filter(p => !assignedIds.has(p.id));
        if (availableAll.length < actualCount) {
          console.warn(`⚠️ 5등: 선정 가능한 참가자 부족 (${availableAll.length}/${actualCount})`);
          selectedParticipants = availableAll; // 가능한 만큼만 선정
        } else {
          selectedParticipants = weightedRandomSelection(availableAll, actualCount);
        }
        console.log(`✅ ${assignedRank}등: 가중치 랜덤으로 ${selectedParticipants.length}명 선정 (계획: ${range.count}명, 실제: ${actualCount}명)`);
        
        normalStats.rank4_5 += selectedParticipants.filter(p => !isSuspiciousReferrer(p.referred_by)).length;
        suspiciousStats.rank4_5 += selectedParticipants.filter(p => isSuspiciousReferrer(p.referred_by)).length;
      } else {
        // 기타 등수
        const availableAll = eligibleParticipants.filter(p => !assignedIds.has(p.id));
        selectedParticipants = weightedRandomSelection(availableAll, actualCount);
        console.log(`✅ ${assignedRank}등: 가중치 랜덤으로 ${actualCount}명 선정`);
        
        normalStats.rank4_5 += selectedParticipants.filter(p => !isSuspiciousReferrer(p.referred_by)).length;
        suspiciousStats.rank4_5 += selectedParticipants.filter(p => isSuspiciousReferrer(p.referred_by)).length;
      }
      
      // 5등 이전까지 배정된 인원 누적 (2차 이벤트의 경우)
      if (is2ndEvent && !is5thRank) {
        totalAssignedBefore5th += selectedParticipants.length;
      }
      
      // 선정된 참가자들에게 등수 배정
      selectedParticipants.forEach(selected => {
        assignedIds.add(selected.id);
        
        const reward = range.prize;
        const targetIndex = updatedParticipants.findIndex((participant) => participant.id === selected.id);
        
        if (targetIndex !== -1) {
          updatedParticipants[targetIndex] = {
            ...updatedParticipants[targetIndex],
            status: 'winner' as const,
            manual_rank: currentSequentialRank, // ✅ 순차 번호 사용 (1, 2, 3, 4...)
            reward_received: reward ?? updatedParticipants[targetIndex].reward_received
          };
          registerParticipantChange(selected.id, {
            status: 'winner',
            manualRank: currentSequentialRank, // ✅ 순차 번호 사용
            rewardReceived: reward
          });
          
          currentSequentialRank++; // ✅ 다음 순차 번호로 증가
        }
      });
    });

    setParticipantsWithOrdering(updatedParticipants);
    
    // ⭐ 랜덤 배정 후 winners state도 업데이트 (당첨자 탭에 표시되도록)
    const winnersFromParticipants = updatedParticipants
      .filter(p => p.status === 'winner' && p.manual_rank !== null && p.manual_rank !== undefined)
      .map((p, index) => ({
        id: p.id,
        event_id: selectedEvent.id,
        participant_id: p.id,
        wallet_address: p.wallet_address,
        user_address: p.wallet_address,
        nickname: p.nickname || '',
        referral_code: p.referral_code || '',
        rank: p.manual_rank || 0,
        prize_amount: p.reward_received || 0,
        payout_status: 'pending',
        tokens_used: p.tokens_used || 0,
        participation_time: p.joined_at || new Date().toISOString()
      }));
    
    console.log(`[랜덤 배정 완료] winners state 업데이트: ${winnersFromParticipants.length}명`);
    setWinners(winnersFromParticipants);
    
    const statsMessage = `
📊 당첨자 통계:
━━━━━━━━━━━━━━━━━━━━
🏆 1~3등 (상위)
  • 정상 계정: ${normalStats.rank1_3}명
  • 의심 계정: ${suspiciousStats.rank1_3}명

🎯 4~5등 (가중치 적용)
  • 정상 계정: ${normalStats.rank4_5}명
  • 의심 계정: ${suspiciousStats.rank4_5}명

━━━━━━━━━━━━━━━━━━━━
✅ 총 ${assignedIds.size}명 선정 완료!
    `.trim();
    
    alert(`✅ 랜덤 배정 완료!\n\n${statsMessage}\n\n전체 저장 버튼을 눌러 반영하세요.`);
  };

  /**
   * 당첨자 배정 초기화 (데이터베이스 포함)
   */
  const handleResetWinners = async () => {
    if (!window.confirm('⚠️ 당첨자 배정을 완전히 초기화하시겠습니까?\n\n• 모든 참여자가 "참여" 상태로 돌아갑니다.\n• 데이터베이스의 당첨자 정보도 삭제됩니다.\n• 이 작업은 되돌릴 수 없습니다!')) {
      return;
    }

    try {
      if (!selectedEvent) return;
      
      // 백엔드 API 호출하여 데이터베이스 초기화
      const response = await fetch(
        buildApiUrl(`/api/admin/boom-events/${selectedEvent.id}/winners/reset`),
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || '초기화 실패');
      }

      // 프론트엔드 상태 초기화
      const resetParticipants = participants.map((participant) => ({
        ...participant,
        status: 'active' as const,
        manual_rank: null,
        reward_received: null
      }));

      setParticipantsWithOrdering(resetParticipants);
      setParticipantChanges({});
      
      alert('✅ 당첨자 배정이 완전히 초기화되었습니다.\n데이터베이스에서 모든 당첨자 정보가 삭제되었습니다.');
      
      // 참가자 목록 새로고침
      await loadParticipants(selectedEvent.id);
    } catch (error) {
      console.error('초기화 오류:', error);
      alert(`초기화 실패: ${error instanceof Error ? error.message : '알 수 없는 오류'}`);
    }
  };

  const hasParticipantChanges = Object.keys(participantChanges).length > 0;
  const activeParticipantCount = participants.filter((participant) => participant.status === 'active').length;
  // winners state가 있으면 우선 사용, 없으면 participants에서 계산
  const winnerParticipantCount = winners.length > 0 ? winners.length : participants.filter((participant) => participant.status === 'winner').length;
  const onHoldParticipantCount = participants.filter((participant) => participant.status === 'on_hold').length;
  const eliminatedParticipantCount = participants.filter((participant) =>
    participant.status === 'eliminated' || participant.status === 'removed'
  ).length;

  const handleParticipantRankSave = (participantId: number) => {
    handleSaveParticipant(participantId);
  };

  const handleDeleteParticipant = async (participantId: number) => {
    if (!selectedEvent) return;
    const confirmDelete = window.confirm('정말로 이 참가자를 삭제하시겠습니까?');
    if (!confirmDelete) return;

    try {
      const response = await fetch(
        buildApiUrl(`/api/admin/boom-events/${selectedEvent.id}/participants/${participantId}`),
        {
          method: 'DELETE'
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.error || `HTTP ${response.status}`);
      }

      alert('참가자를 삭제했습니다.');
      await loadParticipants(selectedEvent.id);
    } catch (error) {
      console.error('참가자 삭제 실패:', error);
      alert('참가자를 삭제하지 못했습니다.');
    }
  };

  const createEventPopup = async () => {
    try {
      if (!selectedEvent) return;

      alert('현재 팝업 기능은 백엔드에서 지원되지 않습니다.');
        setShowPopupModal(false);
        setNewPopup({ title: '', message: '', type: 'info', duration: 5000 });
    } catch (error) {
      console.error('팝업 생성 실패:', error);
      alert('팝업 생성에 실패했습니다.');
    }
  };

  const announceWinners = async (eventId: number) => {
    try {
      const response = await fetch(
        buildApiUrl(`/api/admin/boom-events/${eventId}/announce-winners`),
        {
        method: 'POST'
        }
      );
      
      if (response.ok) {
        alert('당첨자가 발표되었습니다!');
        loadWinners(eventId);
      } else {
        const data = await response.json().catch(() => ({}));
        alert(`당첨자 발표 실패: ${data.error || response.statusText}`);
      }
    } catch (error) {
      console.error('당첨자 발표 실패:', error);
      alert('당첨자 발표 중 오류가 발생했습니다.');
    }
  };

  /**
   * 당첨자 확정 (boom_event_winners 테이블에 스냅샷 생성)
   */
  const handleFinalizeWinners = async (eventId: number) => {
    if (!window.confirm('당첨자를 확정하시겠습니까?\n\n확정 후에는 당첨자 명단이 boom_event_winners 테이블에 저장되며,\n이후 당첨금 지급이 가능합니다.')) {
      return;
    }

    try {
      const response = await fetch(
        buildApiUrl(`/api/admin/boom-events/${eventId}/finalize-winners`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        alert(`✅ 당첨자 확정 완료!\n\n${data.message}`);
      loadWinners(eventId);
      } else {
        const data = await response.json().catch(() => ({}));
        alert(`❌ 당첨자 확정 실패:\n${data.error || response.statusText}`);
      }
    } catch (error) {
      console.error('당첨자 확정 실패:', error);
      alert('당첨자 확정 중 오류가 발생했습니다.');
    }
  };

  /**
   * 당첨금 지급 (블록체인 전송)
   */
  const handlePayWinners = async (eventId: number) => {
    if (!window.confirm('⚠️ 당첨금을 지급하시겠습니까?\n\n이 작업은 블록체인 트랜잭션을 실행하며,\n실행 후 취소할 수 없습니다.\n\npayout_status가 "pending"인 당첨자에게만 지급됩니다.')) {
      return;
    }

    try {
      const response = await fetch(
        buildApiUrl(`/api/admin/boom-events/${eventId}/pay-winners`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({})
        }
      );

      if (response.ok) {
        const data = await response.json();
        const summary = data.summary || {};
        alert(
          `✅ 당첨금 지급 완료!\n\n` +
          `총 ${summary.total || 0}명 처리\n` +
          `성공: ${summary.success || 0}명\n` +
          `실패: ${summary.failed || 0}명\n\n` +
          `상세 내역은 콘솔을 확인해주세요.`
        );
        console.log('당첨금 지급 상세:', data.results);
        loadWinners(eventId);
      } else {
        const data = await response.json().catch(() => ({}));
        alert(`❌ 당첨금 지급 실패:\n${data.error || response.statusText}`);
      }
    } catch (error) {
      console.error('당첨금 지급 실패:', error);
      alert('당첨금 지급 중 오류가 발생했습니다.');
    }
  };

  const simulateParticipation = async (eventId: number) => {
    try {
      const response = await fetch(
        buildApiUrl(`/api/admin/boom-events/${eventId}/join`),
        {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            user_address: '0x' + Math.random().toString(16).substring(2, 42),
            payment_amount:
              events.find(e => e.id === eventId)?.participation_fee || 0,
            payment_type: 'VTTN'
          })
        }
      );
      
      if (response.ok) {
        alert('참여 완료!');
        setShowParticipationCompleteModal(true);
        await Promise.all([loadRealTimeData(), loadParticipants(eventId)]);
      } else {
        const data = await response.json().catch(() => ({}));
        alert(`참여 실패: ${data.error || response.statusText}`);
      }
    } catch (error) {
      console.error('참여 실패:', error);
      alert('참여 중 오류가 발생했습니다.');
    }
  };

  const createEvent = async () => {
    try {
      if (!newEvent.name || !newEvent.start_date || !newEvent.end_date) {
        alert('이벤트 이름과 기간을 입력해 주세요.');
        return;
      }

      const distributionPayload = prizeDistribution
        .filter((tier) => Number(tier.count) > 0)
        .map((tier) => ({
          rank: tier.rank,
          count: Number(tier.count) || 0,
          prize: Number(tier.prize) || 0,
          label: typeof tier.rank === 'string' ? tier.rank : `${tier.rank}위`
        }));

      if (distributionPayload.length === 0) {
        alert('상금 분배를 최소 1개 이상 입력해 주세요.');
        return;
      }

      const entryFee = parseFloat(newEvent.participation_fee || '0');
      const maxParticipants =
        newEvent.max_participants === ''
          ? null
          : parseInt(newEvent.max_participants || '0', 10);
      const priority = parseInt(newEvent.phase || '1', 10) || 0;

      const rulesPayload = {
        summary: `${totalPrizeAmount.toLocaleString()} VTTN 이벤트`,
        entry_requirement: `${entryFee} VTTN 참가비`,
        usage: ['이벤트 참여 가능', '스테이킹 사용 가능', '노드 구독 사용 가능'],
        rounds: newEvent.rounds || '1',
        notes: []
      };

      const payload = {
        title: newEvent.name,
        description: newEvent.description,
        event_type: 'daily_boom',
        entry_fee: entryFee,
        reward_pool: totalPrizeAmount,
        max_participants: maxParticipants,
        start_time: toIsoString(newEvent.start_date),
        end_time: toIsoString(newEvent.end_date),
        priority,
        prize_distribution: { distribution: distributionPayload },
        rules: rulesPayload,
        status: 'active'
      };

      const response = await fetch(buildApiUrl('/api/admin/boom-events'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success) {
        alert('BOOM 이벤트가 생성되었습니다!');
        setShowCreateModal(false);
        setNewEvent({
          name: '',
          description: '',
          start_date: '',
          end_date: '',
          prize_pool: '',
          participation_fee: '',
          max_participants: '',
          rounds: '',
          phase: '1'
        });
        setPrizeDistribution(DEFAULT_PRIZE_DISTRIBUTION.map((tier) => ({ ...tier })));
        loadEvents();
      } else {
        alert('이벤트 생성 실패: ' + (data.error || response.statusText));
      }
    } catch (error) {
      console.error('이벤트 생성 실패:', error);
      alert('이벤트 생성 중 오류가 발생했습니다.');
    }
  };

  const deleteEvent = async (eventId: number) => {
    if (!window.confirm('정말로 이 이벤트를 삭제하시겠습니까?')) {
      return;
    }
    
    try {
      const response = await fetch(
        buildApiUrl(`/api/admin/boom-events/${eventId}`),
        {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
        }
      );
      
      const data = await response.json().catch(() => ({}));
      
      if (response.ok && data.success) {
        alert('이벤트가 성공적으로 삭제되었습니다!');
        loadEvents();
      } else {
        alert('이벤트 삭제 실패: ' + (data.error || response.statusText));
      }
    } catch (error) {
      console.error('이벤트 삭제 실패:', error);
      alert('이벤트 삭제 중 오류가 발생했습니다.');
    }
  };

  const joinEvent = async (eventId: number) => {
    try {
      if (!selectedEvent) return;

      const walletInfo = localStorage.getItem('voltron-wallet');
      if (!walletInfo) {
        alert('지갑 정보를 찾을 수 없습니다.');
        return;
      }
      
      const { address } = JSON.parse(walletInfo);
      
      const response = await fetch(
        buildApiUrl(`/api/admin/boom-events/${eventId}/join`),
        {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            user_address: address,
            payment_amount: selectedEvent.participation_fee,
            payment_type: 'VTTN'
          })
        }
      );

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success) {
        alert('이벤트에 참여하였습니다!');
          loadParticipants(eventId);
        loadRealTimeData();
      } else {
        alert('참여 실패: ' + (data.error || response.statusText));
      }
    } catch (error) {
      console.error('이벤트 참여 실패:', error);
      alert('이벤트 참여 중 오류가 발생했습니다.');
    }
  };

  // 🆕 당첨자 검색 함수 (전체 유저 DB 검색)
  const searchWinners = async (query: string) => {
    if (!query || query.trim().length < 2) {
      setWinnerSearchResults([]);
      return;
    }

    try {
      // 백엔드 API로 전체 유저 검색
      const response = await fetch(
        buildApiUrl(`/api/admin/users/search?query=${encodeURIComponent(query)}`),
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.users) {
          // 검색 결과에 참여 여부 추가
          const usersWithParticipation = data.users.map((user: any) => {
            const existingParticipant = participants.find(p => 
              p.wallet_address.toLowerCase() === user.wallet_address.toLowerCase()
            );
            
            return {
              id: user.id || existingParticipant?.id,
              nickname: user.nickname || existingParticipant?.nickname,
              referral_code: user.referral_code || existingParticipant?.referral_code,
              wallet_address: user.wallet_address,
              tokens_used: existingParticipant?.tokens_used || 0,
              status: existingParticipant?.status || 'not_participated',
              manual_rank: existingParticipant?.manual_rank || null,
              reward_received: existingParticipant?.reward_received || null,
              joined_at: existingParticipant?.joined_at || user.created_at,
              is_participant: !!existingParticipant,
              is_winner: existingParticipant?.status === 'winner'
            };
          });

          // 이미 당첨자인 사람은 제외
          const filtered = usersWithParticipation.filter((u: any) => !u.is_winner);
          setWinnerSearchResults(filtered);
      } else {
          setWinnerSearchResults([]);
        }
      } else {
        // API 실패 시 로컬 participants에서 검색 (fallback)
        const filtered = participants.filter(p => 
          p.status !== 'winner' && (
            p.nickname?.toLowerCase().includes(query.toLowerCase()) ||
            p.referral_code?.toLowerCase().includes(query.toLowerCase()) ||
            p.wallet_address?.toLowerCase().includes(query.toLowerCase())
          )
        );
        setWinnerSearchResults(filtered);
      }
    } catch (error) {
      console.error('검색 오류:', error);
      // 에러 시 로컬 participants에서 검색 (fallback)
      const filtered = participants.filter(p => 
        p.status !== 'winner' && (
          p.nickname?.toLowerCase().includes(query.toLowerCase()) ||
          p.referral_code?.toLowerCase().includes(query.toLowerCase()) ||
          p.wallet_address?.toLowerCase().includes(query.toLowerCase())
        )
      );
      setWinnerSearchResults(filtered);
    }
  };

  // 🆕 당첨자 추가
  const handleAddWinner = async (participant: Participant, rank: number) => {
    try {
      const updatedParticipants = participants.map(p => {
        if (p.id === participant.id) {
          return { ...p, status: 'winner' as const, manual_rank: rank };
        }
        return p;
      });
      
      setParticipants(updatedParticipants);
      setShowWinnerSearchModal(false);
      setWinnerSearchQuery('');
      setWinnerSearchResults([]);
      setSelectedRankForAdd(null);
      
      alert(`${participant.nickname}님을 ${rank}등으로 추가했습니다.`);
    } catch (error) {
      console.error('당첨자 추가 오류:', error);
      alert('당첨자 추가 중 오류가 발생했습니다.');
    }
  };

  // 🆕 당첨자 변경
  const handleChangeWinner = async (oldWinnerId: number, newParticipant: Participant) => {
    try {
      const oldWinner = participants.find(p => p.id === oldWinnerId);
      if (!oldWinner) return;

      const updatedParticipants = participants.map(p => {
        if (p.id === oldWinnerId) {
          return { ...p, status: 'active' as const, manual_rank: null };
        }
        if (p.id === newParticipant.id) {
          return { ...p, status: 'winner' as const, manual_rank: oldWinner.manual_rank };
        }
        return p;
      });
      
      setParticipants(updatedParticipants);
      setShowWinnerSearchModal(false);
      setWinnerSearchQuery('');
      setWinnerSearchResults([]);
      setEditingWinnerId(null);
      
      alert(`${oldWinner.nickname} → ${newParticipant.nickname}로 변경했습니다.`);
    } catch (error) {
      console.error('당첨자 변경 오류:', error);
      alert('당첨자 변경 중 오류가 발생했습니다.');
    }
  };

  // 🆕 당첨자 삭제 (보류)
  const handleRemoveWinner = async (participantId: number) => {
    if (!window.confirm('당첨자를 보류 상태로 변경하시겠습니까?')) return;

    try {
      const updatedParticipants = participants.map(p => {
        if (p.id === participantId) {
          return { ...p, status: 'on_hold' as const, manual_rank: null };
        }
        return p;
      });
      
      setParticipants(updatedParticipants);
      alert('당첨자를 보류 상태로 변경했습니다.');
    } catch (error) {
      console.error('당첨자 삭제 오류:', error);
      alert('당첨자 삭제 중 오류가 발생했습니다.');
    }
  };

  // 🆕 등수 변경
  const handleRankChange = async (participantId: number, newRank: number) => {
    try {
      const updatedParticipants = participants.map(p => {
        if (p.id === participantId) {
          return { ...p, manual_rank: newRank };
        }
        return p;
      });
      
      setParticipants(updatedParticipants);
    } catch (error) {
      console.error('등수 변경 오류:', error);
      alert('등수 변경 중 오류가 발생했습니다.');
    }
  };

  // 이벤트 상태 변경 함수 (준비중/진행중/종료)
  const changeEventStatus = async (eventId: number, status: 'upcoming' | 'active' | 'ended') => {
    try {
      const response = await fetch(
        buildApiUrl(`/api/admin/boom-events/${eventId}/status`),
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status })
        }
      );

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success) {
        const statusText = status === 'upcoming' ? '준비중' : status === 'active' ? '진행중' : '종료';
        alert(`이벤트 상태가 "${statusText}"로 변경되었습니다!`);
        loadEvents();
      } else {
        alert('이벤트 상태 변경 실패: ' + (data.error || response.statusText));
      }
    } catch (error) {
      console.error('이벤트 상태 변경 실패:', error);
      alert('이벤트 상태 변경 중 오류가 발생했습니다.');
    }
  };

  const startEvent = async (eventId: number) => {
    try {
      const response = await fetch(
        buildApiUrl(`/api/admin/boom-events/${eventId}/start`),
        {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        }
        }
      );
      
      const data = await response.json().catch(() => ({}));
      
      if (response.ok && data.success) {
        alert('이벤트가 시작되었습니다!');
        loadEvents();
      } else {
        alert('이벤트 시작 실패: ' + (data.error || response.statusText));
      }
    } catch (error) {
      console.error('이벤트 시작 실패:', error);
      alert('이벤트 시작 중 오류가 발생했습니다.');
    }
  };

  const endEvent = async (eventId: number) => {
    try {
      const response = await fetch(
        buildApiUrl(`/api/admin/boom-events/${eventId}/end`),
        {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        }
        }
      );
      
      const data = await response.json().catch(() => ({}));
      
      if (response.ok && data.success) {
        alert('이벤트가 종료되었습니다!');
        loadEvents();
      } else {
        alert('이벤트 종료 실패: ' + (data.error || response.statusText));
      }
    } catch (error) {
      console.error('이벤트 종료 실패:', error);
      alert('이벤트 종료 중 오류가 발생했습니다.');
    }
  };

  const editEvent = (event: BoomEvent) => {
    setEditingEvent(event);
    setNewEvent({
      name: event.name,
      description: event.description,
      start_date: toLocalDateTimeInput(event.start_date),
      end_date: toLocalDateTimeInput(event.end_date),
      prize_pool: event.prize_pool.toString(),
      participation_fee: event.participation_fee.toString(),
      max_participants: event.max_participants.toString(),
      rounds: event.rounds.toString(),
      phase: event.phase.toString()
    });
    const normalized = normalizePrizeDistribution(
      event.prize_distribution,
      Number(event.prize_pool || 0)
    ).distribution;
    if (normalized.length > 0) {
      setPrizeDistribution(normalized.map((tier) => ({ ...tier })));
    } else {
      setPrizeDistribution(DEFAULT_PRIZE_DISTRIBUTION.map((tier) => ({ ...tier })));
    }
    setShowEditModal(true);
  };

  const updateEvent = async () => {
    if (!editingEvent) return;
    
    try {
      const distributionPayload = prizeDistribution
        .filter((tier) => Number(tier.count) > 0)
        .map((tier) => ({
          rank: tier.rank,
          count: Number(tier.count) || 0,
          prize: Number(tier.prize) || 0,
          label: typeof tier.rank === 'string' ? tier.rank : `${tier.rank}위`
        }));

      if (distributionPayload.length === 0) {
        alert('상금 분배를 최소 1개 이상 입력해 주세요.');
        return;
      }

      const entryFee = parseFloat(newEvent.participation_fee || '0');
      const maxParticipants =
        newEvent.max_participants === ''
          ? null
          : parseInt(newEvent.max_participants || '0', 10);
      const priority = parseInt(newEvent.phase || '1', 10) || 0;
      const existingRules = normalizeRules(editingEvent.rules);
      const rulesPayload = {
        ...existingRules,
        summary:
          existingRules.summary || `${totalPrizeAmount.toLocaleString()} VTTN 이벤트`,
        entry_requirement: `${entryFee} VTTN 참가비`,
        rounds: newEvent.rounds || existingRules.rounds || '1'
      };

      const payload = {
        title: newEvent.name,
        description: newEvent.description,
        entry_fee: entryFee,
        reward_pool: totalPrizeAmount,
        max_participants: maxParticipants,
        start_time: toIsoString(newEvent.start_date),
        end_time: toIsoString(newEvent.end_date),
        priority,
        prize_distribution: { distribution: distributionPayload },
        rules: rulesPayload,
        status: editingEvent.status
      };

      const response = await fetch(
        buildApiUrl(`/api/admin/boom-events/${editingEvent.id}`),
        {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success) {
        alert('이벤트가 수정되었습니다!');
        setShowEditModal(false);
        setEditingEvent(null);
        setNewEvent({
          name: '',
          description: '',
          start_date: '',
          end_date: '',
          prize_pool: '',
          participation_fee: '',
          max_participants: '',
          rounds: '',
          phase: '1'
        });
        setPrizeDistribution(DEFAULT_PRIZE_DISTRIBUTION.map((tier) => ({ ...tier })));
        loadEvents();
      } else {
        alert('이벤트 수정 실패: ' + (data.error || response.statusText));
      }
    } catch (error) {
      console.error('이벤트 수정 실패:', error);
      alert('이벤트 수정 중 오류가 발생했습니다.');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'upcoming': return 'bg-blue-100 text-blue-800';
      case 'active': return 'bg-green-100 text-green-800';
      case 'ended': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'upcoming': return <Clock className="w-4 h-4" />;
      case 'active': return <Play className="w-4 h-4" />;
      case 'ended': return <Pause className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const filteredEvents = events.filter(event => {
    const matchesStatus = statusFilter === 'all' || event.status === statusFilter;
    const matchesSearch = event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Seoul'
    }).format(date);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Target className="w-8 h-8 text-orange-500" />
          <h1 className="text-3xl font-bold text-gray-900">BOOM Events</h1>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Create Event</span>
        </button>
      </div>

      {/* 필터 및 검색 */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="all">All Status</option>
              <option value="upcoming">Upcoming</option>
              <option value="active">Active</option>
              <option value="ended">Ended</option>
            </select>
          </div>
          <div className="flex-1 relative">
            <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search events..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
        </div>
      </div>

      {/* 이벤트 목록 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredEvents.map((event) => (
          <div key={event.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
            {/* 상태 버튼 3개 */}
            <div className="flex border-b border-gray-200">
                    <button
                onClick={() => changeEventStatus(event.id, 'upcoming')}
                className={`flex-1 py-3 text-sm font-medium transition-colors ${
                  event.status === 'upcoming'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                이벤트 준비중
                    </button>
                    <button
                onClick={() => changeEventStatus(event.id, 'active')}
                className={`flex-1 py-3 text-sm font-medium transition-colors border-x border-gray-200 ${
                  event.status === 'active'
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                이벤트 중
                    </button>
              <button
                onClick={() => changeEventStatus(event.id, 'ended')}
                className={`flex-1 py-3 text-sm font-medium transition-colors ${
                  event.status === 'ended'
                    ? 'bg-gray-500 text-white'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                이벤트 종료
              </button>
            </div>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(event.status)}`}>
                  {getStatusIcon(event.status)}
                  <span className="capitalize">
                    {event.status === 'upcoming' ? '준비중' : 
                     event.status === 'active' ? '진행중' : 
                     event.status === 'ended' ? '종료' : event.status}
                  </span>
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => editEvent(event)}
                    className="p-2 text-blue-500 hover:text-blue-600 transition-colors"
                    title="이벤트 수정"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={async () => {
                      setSelectedEvent(event);
                      await Promise.all([
                        loadParticipants(event.id),
                        loadWinners(event.id)
                      ]);
                    }}
                    className="p-2 text-gray-500 hover:text-orange-500 transition-colors"
                    title="이벤트 상세보기"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <h3 className="text-xl font-bold text-gray-900 mb-2">{event.name}</h3>
              <p className="text-gray-600 text-sm mb-2 line-clamp-2">{event.description}</p>
              
              {/* 카운트다운 */}
              <div className="bg-gradient-to-r from-orange-100 to-red-100 border border-orange-200 rounded-lg p-3 mb-4">
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-orange-600" />
                  <span className="text-sm font-medium text-orange-800">
                    {countdowns[event.id] || '계산 중...'}
                  </span>
                </div>
              </div>
              
              {/* 실시간 참여 현황 */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Users className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-800">실시간 참여자</span>
                  </div>
                  <span className="text-lg font-bold text-blue-900">
                    {realTimeParticipants[event.id] || 0} / {event.max_participants}
                  </span>
                </div>
                <div className="mt-2">
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${((realTimeParticipants[event.id] || 0) / event.max_participants) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Prize Pool:</span>
                  <span className="font-medium text-green-600">{parseFloat(event.prize_pool || '0').toFixed(2)} VTTN</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Entry Fee:</span>
                  <span className="font-medium">{parseFloat(event.participation_fee || '0').toFixed(2)} VTTN</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Participants:</span>
                  <span className="font-medium">{event.participants_count}/{event.max_participants}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Rounds:</span>
                  <span className="font-medium">{event.rounds}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Phase:</span>
                  <span className="font-medium">{event.phase}차</span>
                </div>
              </div>
              
              {/* 시상 정보 */}
              {(() => {
                const tierRanges = computeTierRangesForEvent(event);
                if (!tierRanges.length) return null;
                return (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Trophy className="w-4 h-4 text-yellow-600" />
                    <span className="text-sm font-medium text-yellow-800">Prize Distribution</span>
                  </div>
                  <div className="space-y-1">
                      {tierRanges.slice(0, 3).map((range, index) => (
                      <div key={index} className="flex justify-between text-xs text-yellow-700">
                          <span>{range.label} ({range.count}명)</span>
                          <span className="font-medium">{range.prize.toFixed(2)} VTTN</span>
                      </div>
                    ))}
                      {tierRanges.length > 3 && (
                        <div className="text-xs text-yellow-600">+ {tierRanges.length - 3} more tiers</div>
                    )}
                  </div>
                </div>
                );
              })()}
              
              <div className="space-y-2 mb-4">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span>Start: {formatDate(event.start_date)}</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span>End: {formatDate(event.end_date)}</span>
                </div>
              </div>
              
              {/* 액션 버튼들 */}
              <div className="grid grid-cols-2 gap-2 mb-4">
                {/* 🆕 통합 이벤트 관리 버튼 */}
                <button
                  onClick={async () => {
                    setSelectedEvent(event);
                    await loadParticipants(event.id);
                    await loadWinners(event.id);
                    setManagementTab('info');
                    setShowEventManagementModal(true);
                  }}
                  className="col-span-2 flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-colors font-semibold shadow-md"
                >
                  <Trophy className="w-5 h-5" />
                  <span>🏆 이벤트 관리</span>
                </button>
                
                <button
                  onClick={async () => {
                    setSelectedEvent(event);
                    await loadParticipants(event.id);
                    setShowParticipantsModal(true);
                  }}
                  className="flex items-center justify-center space-x-1 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                >
                  <Users className="w-4 h-4" />
                  <span>참여자 (구)</span>
                </button>
                <button
                  onClick={async () => {
                    setSelectedEvent(event);
                    await loadWinners(event.id);
                    setShowWinnersModal(true);
                  }}
                  className="flex items-center justify-center space-x-1 px-3 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors text-sm"
                >
                  <Trophy className="w-4 h-4" />
                  <span>당첨자 (구)</span>
                </button>
                <button
                  onClick={() => {
                    setSelectedEvent(event);
                    setShowPopupModal(true);
                  }}
                  className="flex items-center justify-center space-x-1 px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-sm"
                >
                  <Star className="w-4 h-4" />
                  <span>팝업</span>
                </button>
                <button
                  onClick={() => simulateParticipation(event.id)}
                  className="flex items-center justify-center space-x-1 px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm"
                >
                  <Target className="w-4 h-4" />
                  <span>참여</span>
                </button>
              </div>
              
              {event.status === 'active' && (
                <button
                  onClick={() => announceWinners(event.id)}
                  className="w-full bg-orange-500 text-white py-2 px-4 rounded-lg hover:bg-orange-600 transition-colors font-medium"
                >
                  Join Event
                </button>
              )}
              
              {event.status === 'upcoming' && (
                <div className="w-full bg-orange-100 text-orange-800 py-2 px-4 rounded-lg text-center font-medium">
                  🕐 이벤트 시작 대기중
                </div>
              )}
              
              {event.status === 'active' && (
                <div className="w-full bg-green-100 text-green-800 py-2 px-4 rounded-lg text-center font-medium">
                  🔥 이벤트 진행중
                </div>
              )}
              
              {event.status === 'ended' && (
                <div className="w-full bg-gray-100 text-gray-600 py-2 px-4 rounded-lg text-center font-medium">
                  ✅ 이벤트 종료
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredEvents.length === 0 && (
        <div className="text-center py-12">
          <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-600 mb-2">No events found</h2>
          <p className="text-gray-500">Try adjusting your filters or create a new event.</p>
        </div>
      )}

      {/* 이벤트 상세 모달 */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">{selectedEvent.name}</h2>
                <button
                  onClick={() => setSelectedEvent(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Event Details</h3>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm text-gray-500">Description:</span>
                      <p className="text-gray-900">{selectedEvent.description}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-gray-500">Prize Pool:</span>
                        <p className="font-medium text-green-600">{selectedEvent.prize_pool} VTTN</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">Entry Fee:</span>
                        <p className="font-medium">{selectedEvent.participation_fee} VTTN</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-gray-500">Participants:</span>
                        <p className="font-medium">{selectedEvent.participants_count}/{selectedEvent.max_participants}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">Rounds:</span>
                        <p className="font-medium">{selectedEvent.rounds}</p>
                      </div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Duration:</span>
                      <p className="text-gray-900">
                        {formatDate(selectedEvent.start_date)} - {formatDate(selectedEvent.end_date)}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-4">Participants</h3>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-700">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">총 참여자</span>
                        <span className="font-semibold text-blue-700">{participantsTotal || participants.length}명</span>
                          </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">현재 표시 중</span>
                        <span className="font-semibold text-blue-700">{participants.length}명</span>
                          </div>
                        </div>
                        </div>
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={() => setShowParticipantsModal(true)}
                      className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      <Users className="w-5 h-5" />
                      <span>참여자 관리 열기</span>
                    </button>
                    <button
                      onClick={() => setShowWinnersModal(true)}
                      className="flex items-center justify-center gap-2 px-4 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
                    >
                      <Trophy className="w-5 h-5" />
                      <span>당첨자 관리 열기</span>
                    </button>
                      </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 이벤트 생성 모달 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Create BOOM Event</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Event Name</label>
                  <input
                    type="text"
                    value={newEvent.name}
                    onChange={(e) => setNewEvent({...newEvent, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Enter event name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Describe your event"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                    <input
                      type="datetime-local"
                      value={newEvent.start_date}
                      onChange={(e) => setNewEvent({...newEvent, start_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                    <input
                      type="datetime-local"
                      value={newEvent.end_date}
                      onChange={(e) => setNewEvent({...newEvent, end_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prize Pool (VTTN)
                    </label>
                    <input
                      type="text"
                      value={totalPrizeAmount.toLocaleString()}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 focus:ring-orange-500 focus:border-orange-500"
                    />
                    <div className="text-xs text-gray-500 mt-1">
                      등수 및 상금 입력에 따라 자동 계산됩니다.
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Entry Fee (VTTN)</label>
                    <input
                      type="number"
                      value={newEvent.participation_fee}
                      onChange={(e) => setNewEvent({...newEvent, participation_fee: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                      placeholder="0"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Max Participants</label>
                    <input
                      type="number"
                      value={newEvent.max_participants}
                      onChange={(e) => setNewEvent({...newEvent, max_participants: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                      placeholder="100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Rounds</label>
                    <input
                      type="number"
                      value={newEvent.rounds}
                      onChange={(e) => setNewEvent({...newEvent, rounds: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                      placeholder="4"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phase</label>
                    <input
                      type="number"
                      value={newEvent.phase}
                      onChange={(e) => setNewEvent({...newEvent, phase: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                      placeholder="1"
                    />
                  </div>
                </div>
                
                {/* 시상 정보 섹션 */}
                <div className="border-t pt-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                    <Trophy className="w-5 h-5 text-yellow-500" />
                    <span>Prize Distribution</span>
                  </h3>
                  {renderPrizeDistributionEditor()}
                </div>
                
                <div className="flex space-x-4 pt-4">
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={createEvent}
                    className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                  >
                    Create Event
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 이벤트 수정 모달 */}
      {showEditModal && editingEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Edit BOOM Event</h2>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingEvent(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Event Name</label>
                  <input
                    type="text"
                    value={newEvent.name}
                    onChange={(e) => setNewEvent({...newEvent, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Enter event name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Describe your event"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                    <input
                      type="datetime-local"
                      value={newEvent.start_date}
                      onChange={(e) => setNewEvent({...newEvent, start_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                    <input
                      type="datetime-local"
                      value={newEvent.end_date}
                      onChange={(e) => setNewEvent({...newEvent, end_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prize Pool (VTTN)
                    </label>
                    <input
                      type="text"
                      value={totalPrizeAmount.toLocaleString()}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 focus:ring-orange-500 focus:border-orange-500"
                    />
                    <div className="text-xs text-gray-500 mt-1">
                      등수 및 상금 입력에 따라 자동 계산됩니다.
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Entry Fee (VTTN)</label>
                    <input
                      type="number"
                      value={newEvent.participation_fee}
                      onChange={(e) => setNewEvent({...newEvent, participation_fee: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                      placeholder="0"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Max Participants</label>
                    <input
                      type="number"
                      value={newEvent.max_participants}
                      onChange={(e) => setNewEvent({...newEvent, max_participants: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                      placeholder="100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Rounds</label>
                    <input
                      type="number"
                      value={newEvent.rounds}
                      onChange={(e) => setNewEvent({...newEvent, rounds: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                      placeholder="4"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phase</label>
                    <input
                      type="number"
                      value={newEvent.phase}
                      onChange={(e) => setNewEvent({...newEvent, phase: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                      placeholder="1"
                    />
                  </div>
                </div>
                
                {/* 시상 정보 섹션 */}
                <div className="border-t pt-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                    <Trophy className="w-5 h-5 text-yellow-500" />
                    <span>Prize Distribution (당첨 등수별 상금 설정)</span>
                  </h3>
                  {renderPrizeDistributionEditor()}
                </div>
                
                <div className="flex space-x-4 pt-4">
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingEvent(null);
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={updateEvent}
                    className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Update Event
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 실시간 참여자 모달 */}
      {showParticipantsModal && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-7xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex flex-col gap-4 mb-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <h3 className="text-xl font-bold text-gray-900">
                  👥 {selectedEvent.name} - 실시간 참여자 ({participantsTotal || participants.length}명 / {selectedEvent.max_participants}명)
                </h3>
                <div className="flex flex-col lg:flex-row lg:items-center gap-2 w-full lg:w-auto">
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={handleRandomAssignWinners}
                      className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium"
                    >
                      랜덤 배정
                    </button>
                    <button
                      onClick={handleResetWinners}
                      className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm font-medium"
                    >
                      초기화
                    </button>
                    <button
                      onClick={handleBulkSave}
                      disabled={!hasParticipantChanges}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        hasParticipantChanges
                          ? 'bg-green-500 text-white hover:bg-green-600'
                          : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      전체 저장
                    </button>
                  </div>
                  <div className="w-full lg:w-64">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={participantModalSearch}
                        onChange={(e) => setParticipantModalSearch(e.target.value)}
                        placeholder="닉네임·추천코드·지갑 검색"
                        className="w-full pl-10 pr-8 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-500 text-sm bg-white"
                      />
                      {participantModalSearch && (
                        <button
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-gray-700"
                          onClick={() => setParticipantModalSearch('')}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setShowParticipantsModal(false)}
                    className="p-2 rounded-full border border-gray-200 hover:bg-gray-100 transition-colors text-gray-500 self-start lg:self-auto"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                {/* 상단 통계 */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm mb-4">
                <div className="bg-white p-3 rounded border text-center">
                    <div className="font-bold text-2xl text-blue-600">{participantsTotal || participants.length}</div>
                  <div className="text-gray-600">총 참여자</div>
                </div>
                <div className="bg-white p-3 rounded border text-center">
                    <div className="font-bold text-2xl text-green-600">{selectedEvent?.prize_pool ? Number(selectedEvent.prize_pool).toLocaleString() : '0'}</div>
                    <div className="text-gray-600">총상금 VTTN</div>
                </div>
                <div className="bg-white p-3 rounded border text-center">
                    <div className="font-bold text-2xl text-orange-500">{winnerParticipantCount}</div>
                  <div className="text-gray-600">당첨자</div>
                </div>
                <div className="bg-white p-3 rounded border text-center">
                    <div className="font-bold text-2xl text-purple-500">{onHoldParticipantCount}</div>
                    <div className="text-gray-600">보류</div>
                  </div>
                  <div className="bg-white p-3 rounded border text-center">
                    <div className="font-bold text-2xl text-gray-600">{eliminatedParticipantCount}</div>
                    <div className="text-gray-600">탈락</div>
                  </div>
                </div>

                {/* 등수별 배정 현황 */}
                <div className="grid grid-cols-5 gap-2">
                  {selectedEvent && (() => {
                    const prizePoolAmount = Number(selectedEvent.prize_pool || 0);
                    const distribution = normalizePrizeDistribution(
                      selectedEvent.prize_distribution,
                      prizePoolAmount
                    ).distribution;
                    const tierRanges = buildTierRanges(distribution, prizePoolAmount);
                    
                    return tierRanges.map((range, idx) => {
                      const assignedCount = participants.filter(p => 
                        p.status === 'winner' && 
                        p.manual_rank !== null && 
                        p.manual_rank !== undefined &&
                        p.manual_rank >= range.start && 
                        p.manual_rank <= range.end
                      ).length;
                      const isComplete = assignedCount === range.count;
                      
                      return (
                        <div key={idx} className="flex flex-col items-center justify-center gap-1 px-3 py-2 bg-white rounded-lg border border-gray-200 text-xs">
                          <span className="font-bold text-gray-800">{range.label}</span>
                          <div className="flex items-center gap-1">
                            <span className="text-gray-600">{range.prize.toLocaleString()} VTTN</span>
                            <span className="text-gray-500">×{range.count}명</span>
                            <span className={`font-medium ${isComplete ? 'text-green-600' : 'text-orange-500'}`}>
                              ({assignedCount}/{range.count})
                            </span>
                            {isComplete && <span className="text-green-500">✅</span>}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-200">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-200 px-4 py-3 text-left font-bold w-16">번호</th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-bold w-24">등수</th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-bold">닉네임</th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-bold">추천 코드</th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-bold">지갑 주소</th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-bold">참가비</th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-bold">상태</th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-bold">참여 시간</th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-bold">관리</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredParticipantsForModal.map((participant) => {
                    const normalizedStatus =
                      participant.status === 'removed' ? 'eliminated' : participant.status;
                    return (
                      <tr
                        key={participant.id}
                        className={`hover:bg-gray-50 ${
                          normalizedStatus === 'winner'
                            ? 'bg-yellow-50'
                            : normalizedStatus === 'on_hold'
                              ? 'bg-purple-50'
                              : normalizedStatus === 'eliminated'
                                ? 'bg-red-50'
                                : ''
                        }`}
                      >
                        <td className="border border-gray-200 px-4 py-3 text-sm text-gray-500">
                          {participant.display_order}
                        </td>
                      <td className="border border-gray-200 px-4 py-3">
                          <input
                            type="number"
                            min={1}
                            className="w-20 rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                            value={participant.manual_rank ?? ''}
                            placeholder="-"
                            onChange={(e) => handleParticipantRankChange(participant.id, e.target.value)}
                          />
                      </td>
                        <td className="border border-gray-200 px-4 py-2 text-sm font-medium">
                        {participant.nickname}
                      </td>
                        <td className="border border-gray-200 px-4 py-2 text-sm text-gray-700">
                          {participant.referral_code || '-'}
                        </td>
                        <td className="border border-gray-200 px-4 py-2 font-mono text-sm">
                        {participant.wallet_address.slice(0, 8)}...{participant.wallet_address.slice(-6)}
                      </td>
                        <td className="border border-gray-200 px-4 py-2 text-sm text-gray-600">
                          {participant.tokens_used} VTTN
                      </td>
                        <td className="border border-gray-200 px-4 py-2">
                          <div className="flex gap-1">
                            {PARTICIPANT_STATUS_OPTIONS.filter(opt => opt.key !== 'on_hold').map((option) => {
                              const isSelected =
                                option.key === 'eliminated'
                                  ? normalizedStatus === 'eliminated'
                                  : normalizedStatus === option.key;
                              return (
                                <button
                                  key={option.key}
                                  onClick={() => handleParticipantStatusChange(participant.id, option.key)}
                                  className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                                    isSelected
                                      ? `${option.color} text-white`
                                      : 'bg-gray-100 text-gray-600'
                                  }`}
                                >
                                  {option.label}
                                </button>
                              );
                            })}
                        </div>
                      </td>
                        <td className="border border-gray-200 px-4 py-2 text-xs text-gray-600 whitespace-nowrap">
                          {new Date(participant.joined_at).toLocaleString('ko-KR', { 
                            year: '2-digit', 
                            month: '2-digit', 
                            day: '2-digit', 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                      </td>
                        <td className="border border-gray-200 px-4 py-2">
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleParticipantRankSave(participant.id)}
                              className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                            >
                              저장
                            </button>
                            <button
                              onClick={() => handleQuickHold(participant.id)}
                              className="px-2 py-1 text-xs bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors"
                            >
                              보류
                            </button>
                            <button
                              onClick={() => handleDeleteParticipant(participant.id)}
                              className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                            >
                              삭제
                            </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                  {filteredParticipantsForModal.length === 0 && (
                    <tr>
                      <td colSpan={9} className="border-b border-gray-200 px-4 py-8 text-center text-gray-500">
                        검색 결과가 없습니다.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* 당첨자 발표 모달 */}
      {showWinnersModal && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-7xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                🏆 {selectedEvent.name} - 당첨자 발표 ({winners.length}명)
              </h3>
              <div className="flex items-center gap-2 w-full md:w-auto">
                <div className="relative flex-1 md:w-64">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={winnerSearchTerm}
                    onChange={(e) => setWinnerSearchTerm(e.target.value)}
                    placeholder="닉네임·추천코드·지갑 검색"
                    className="w-full pl-10 pr-8 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-green-500 text-sm bg-white"
                  />
                  {winnerSearchTerm && (
                    <button
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-gray-700"
                      onClick={() => setWinnerSearchTerm('')}
                    >
                      ✕
                    </button>
                  )}
                </div>
                <button
                  onClick={() => setShowWinnersModal(false)}
                  className="p-2 rounded-full border border-gray-200 hover:bg-gray-100 transition-colors text-gray-500"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* 당첨금 지급 정보 */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-2 mb-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                <h4 className="font-bold text-green-800">당첨금 지급 정보</h4>
              </div>
              {(() => {
                const rules = normalizeRules(selectedEvent.rules);
                const usageList: string[] = Array.isArray(rules.usage) ? rules.usage : [];
                const withdrawal = rules.withdrawal || {};
                const notes: string[] = Array.isArray(rules.notes) ? rules.notes : [];
                return (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="bg-white p-3 rounded border">
                      <div className="font-medium text-gray-700">요약 / 참가 조건</div>
                      <div className="text-gray-600">
                        {rules.summary || `${selectedEvent.prize_pool} VTTN 상금 이벤트`}
                      </div>
                      {rules.entry_requirement && (
                        <div className="text-gray-600 mt-1">• {rules.entry_requirement}</div>
                      )}
                </div>
                <div className="bg-white p-3 rounded border">
                      <div className="font-medium text-gray-700">사용 제한</div>
                      {usageList.length > 0 ? (
                        usageList.map((item, idx) => (
                          <div key={idx} className="text-gray-600">
                            • {item}
                          </div>
                        ))
                      ) : (
                        <div className="text-gray-400">등록된 사용 제한 정보가 없습니다.</div>
                      )}
                </div>
                <div className="bg-white p-3 rounded border">
                      <div className="font-medium text-gray-700">출금 / 기타</div>
                      {withdrawal.start && (
                        <div className="text-gray-600">• 출금 시작일: {withdrawal.start}</div>
                      )}
                      {withdrawal.schedule && (
                        <div className="text-gray-600">• 스케줄: {withdrawal.schedule}</div>
                      )}
                      {withdrawal.monthly_limit && (
                        <div className="text-gray-600">• 월 한도: {withdrawal.monthly_limit}</div>
                      )}
                      {notes.length > 0 &&
                        notes.map((item, idx) => (
                          <div key={idx} className="text-gray-600">
                            • {item}
                </div>
                        ))}
                      {withdrawal.start === undefined &&
                        withdrawal.schedule === undefined &&
                        withdrawal.monthly_limit === undefined &&
                        notes.length === 0 && (
                          <div className="text-gray-400">추가 정보가 없습니다.</div>
                        )}
              </div>
                  </div>
                );
              })()}
            </div>
            
            {(() => {
              const tierRanges = computeTierRangesForEvent(selectedEvent);
              if (!tierRanges.length) return null;
              return (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center space-x-2 mb-2">
                    <Trophy className="w-5 h-5 text-orange-500" />
                    <h4 className="font-bold text-orange-700">상금 분배 구조</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                    {tierRanges.map((range, index) => (
                      <div key={index} className="bg-white border border-orange-200 rounded p-3">
                        <div className="text-xs text-gray-500">등급</div>
                        <div className="font-semibold text-gray-800">{range.label}</div>
                        <div className="mt-2 text-xs text-gray-500">인원</div>
                        <div className="font-semibold text-gray-800">{range.count}명</div>
                        <div className="mt-2 text-xs text-gray-500">상금</div>
                        <div className="font-semibold text-orange-600">
                          {range.prize ? `${range.prize.toLocaleString()} VTTN` : '-'}
                        </div>
                        <div className="mt-2 text-xs text-gray-400">
                          순위 {range.start} ~ {range.end}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {winners.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-200">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-200 px-4 py-3 text-left font-bold">순위</th>
                      <th className="border border-gray-200 px-4 py-3 text-left font-bold">닉네임</th>
                      <th className="border border-gray-200 px-4 py-3 text-left font-bold">추천코드</th>
                      <th className="border border-gray-200 px-4 py-3 text-left font-bold">지갑주소</th>
                      <th className="border border-gray-200 px-4 py-3 text-left font-bold">당첨금</th>
                      <th className="border border-gray-200 px-4 py-3 text-left font-bold">락업 해제일</th>
                      <th className="border border-gray-200 px-4 py-3 text-left font-bold">출금 스케줄</th>
                      <th className="border border-gray-200 px-4 py-3 text-left font-bold">발표일시</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredWinnersList.map((winner) => {
                      const tierInfo = findTierInfoForRank(
                        winner.rank,
                        normalizePrizeDistribution(selectedEvent.prize_distribution).distribution,
                        Number(selectedEvent.prize_pool || 0)
                      );
                      return (
                      <tr key={winner.id} className={`hover:bg-gray-50 ${
                        winner.rank === 1 ? 'bg-yellow-50' :
                        winner.rank === 2 ? 'bg-gray-50' :
                        winner.rank === 3 ? 'bg-orange-50' : ''
                      }`}>
                        <td className="border border-gray-200 px-3 py-2 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center font-semibold text-xs ${
                              winner.rank === 1 ? 'bg-yellow-300 text-yellow-900' :
                              winner.rank === 2 ? 'bg-gray-300 text-gray-900' :
                              winner.rank === 3 ? 'bg-orange-300 text-orange-900' :
                              'bg-blue-300 text-blue-900'
                          }`}>
                            {winner.rank}
                            </div>
                            {tierInfo && (
                              <span className="text-xs text-gray-600">{tierInfo.label}</span>
                            )}
                          </div>
                        </td>
                        <td className="border border-gray-200 px-3 py-2 font-medium whitespace-nowrap">
                          {winner.nickname}
                        </td>
                        <td className="border border-gray-200 px-3 py-2 font-mono text-xs whitespace-nowrap">
                          {winner.referral_code}
                        </td>
                        <td className="border border-gray-200 px-3 py-2 font-mono text-xs whitespace-nowrap">
                          {winner.wallet_address.slice(0, 8)}...{winner.wallet_address.slice(-6)}
                        </td>
                        <td className="border border-gray-200 px-3 py-2 whitespace-nowrap">
                          <div className="font-semibold text-green-600 text-sm">
                            {(tierInfo ? tierInfo.prize : winner.prize_amount).toLocaleString()} VTTN
                          </div>
                        </td>
                        <td className="border border-gray-200 px-3 py-2 text-xs text-red-600 whitespace-nowrap">
                            2026.01.30
                        </td>
                        <td className="border border-gray-200 px-3 py-2 text-xs text-gray-600 whitespace-nowrap">
                          매월 10% 출금 · 10개월 분할
                        </td>
                        <td className="border border-gray-200 px-3 py-2 text-xs text-gray-600 whitespace-nowrap">
                          {winner.announced_at ? new Date(winner.announced_at).toLocaleString() : '-'}
                        </td>
                      </tr>
                    )})}
                    {filteredWinnersList.length === 0 && (
                      <tr>
                        <td colSpan={8} className="border-b border-gray-200 px-4 py-8 text-center text-gray-500">
                          검색 결과가 없습니다.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">아직 당첨자가 발표되지 않았습니다.</p>
                <button
                  onClick={() => announceWinners(selectedEvent.id)}
                  className="mt-4 px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                >
                  당첨자 발표하기
                </button>
              </div>
            )}

            {/* 당첨자 관리 버튼 (당첨자가 있을 때만 표시) */}
            {winners.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200 flex gap-4 justify-center">
                <button
                  onClick={() => handleFinalizeWinners(selectedEvent.id)}
                  className="px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-md flex items-center gap-2"
                >
                  <Trophy className="w-5 h-5" />
                  당첨자 확정 (DB 저장)
                </button>
                <button
                  onClick={() => handlePayWinners(selectedEvent.id)}
                  className="px-8 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors shadow-md flex items-center gap-2"
                >
                  <DollarSign className="w-5 h-5" />
                  당첨금 지급 (블록체인)
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 이벤트 팝업 생성 모달 */}
      {showPopupModal && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                이벤트 팝업 생성
              </h3>
              <button
                onClick={() => setShowPopupModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <Trash2 className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  팝업 제목
                </label>
                <input
                  type="text"
                  value={newPopup.title}
                  onChange={(e) => setNewPopup({ ...newPopup, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                  placeholder="예: 이벤트 시작 알림"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  팝업 메시지
                </label>
                <textarea
                  value={newPopup.message}
                  onChange={(e) => setNewPopup({ ...newPopup, message: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                  placeholder="팝업에 표시될 메시지를 입력하세요..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  팝업 타입
                </label>
                <select
                  value={newPopup.type}
                  onChange={(e) => setNewPopup({ ...newPopup, type: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="info">정보</option>
                  <option value="success">성공</option>
                  <option value="warning">경고</option>
                  <option value="error">오류</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  표시 시간 (밀리초)
                </label>
                <input
                  type="number"
                  value={newPopup.duration}
                  onChange={(e) => setNewPopup({ ...newPopup, duration: parseInt(e.target.value) || 5000 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                  placeholder="5000"
                />
              </div>
            </div>
            
            <div className="flex space-x-4 mt-6">
              <button
                onClick={() => setShowPopupModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                취소
              </button>
              <button
                onClick={createEventPopup}
                className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
              >
                팝업 생성
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 참여 완료 팝업 */}
      {showParticipationCompleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                🎉 참여 완료!
              </h3>
              <p className="text-gray-600 mb-6">
                BOOM 이벤트 참여가 성공적으로 완료되었습니다.<br/>
                행운을 빕니다!
              </p>
              <button
                onClick={() => setShowParticipationCompleteModal(false)}
                className="w-full px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🆕 통합 이벤트 관리 팝업 */}
      {showEventManagementModal && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-7xl mx-4 max-h-[95vh] flex flex-col">
            {/* 헤더 */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">🏆 {selectedEvent.name}</h2>
                <p className="text-sm text-gray-500 mt-1">
                  총 {participantsTotal || participants.length}명 · 당첨자 {winnerParticipantCount}명
                </p>
              </div>
              <button
                onClick={() => setShowEventManagementModal(false)}
                className="p-2 rounded-full border border-gray-200 hover:bg-gray-100 transition-colors text-gray-500"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* 탭 네비게이션 */}
            <div className="flex border-b border-gray-200 px-6">
              <button
                onClick={() => setManagementTab('info')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  managementTab === 'info'
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                📊 이벤트 정보
              </button>
              <button
                onClick={() => setManagementTab('participants')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  managementTab === 'participants'
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                👥 전체 참가자
              </button>
              <button
                onClick={() => setManagementTab('winners')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  managementTab === 'winners'
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                🎯 당첨자 관리
              </button>
              <button
                onClick={() => setManagementTab('payment')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  managementTab === 'payment'
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                💰 당첨금 지급관리
              </button>
              <button
                onClick={() => setManagementTab('stats')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  managementTab === 'stats'
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                📈 전체 이벤트 통계
              </button>
            </div>

            {/* 탭 컨텐츠 */}
            <div className="flex-1 overflow-y-auto p-6">
              {managementTab === 'info' && (
                <div className="space-y-6">
                  {/* 기본 정보 */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                      <h3 className="text-lg font-semibold mb-4 text-gray-900">📋 이벤트 기본 정보</h3>
                      <div className="space-y-3">
                        <div>
                          <span className="text-sm text-gray-500">Description:</span>
                          <p className="text-gray-900 mt-1">{selectedEvent.description}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <span className="text-sm text-gray-500">Prize Pool:</span>
                            <p className="font-semibold text-green-600 mt-1">{selectedEvent.prize_pool} VTTN</p>
                          </div>
                          <div>
                            <span className="text-sm text-gray-500">Entry Fee:</span>
                            <p className="font-medium mt-1">{selectedEvent.participation_fee} VTTN</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <span className="text-sm text-gray-500">Participants:</span>
                            <p className="font-medium mt-1">{selectedEvent.participants_count}/{selectedEvent.max_participants}</p>
                          </div>
                          <div>
                            <span className="text-sm text-gray-500">Rounds:</span>
                            <p className="font-medium mt-1">{selectedEvent.rounds}</p>
                          </div>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500">Duration:</span>
                          <p className="text-gray-900 mt-1">
                            {formatDate(selectedEvent.start_date)} ~ {formatDate(selectedEvent.end_date)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* 당첨금 지급 정보 */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                      <div className="flex items-center space-x-2 mb-4">
                        <DollarSign className="w-5 h-5 text-green-600" />
                        <h3 className="text-lg font-semibold text-green-800">💰 당첨금 지급 정보</h3>
                      </div>
                      {(() => {
                        const rules = normalizeRules(selectedEvent.rules);
                        const usageList: string[] = Array.isArray(rules.usage) ? rules.usage : [];
                        const withdrawal = rules.withdrawal || {};
                        const notes: string[] = Array.isArray(rules.notes) ? rules.notes : [];
                        return (
                          <div className="space-y-4">
                            <div className="bg-white p-3 rounded border border-green-100">
                              <div className="font-medium text-gray-700 mb-1">요약 / 참가 조건</div>
                              <div className="text-sm text-gray-600">
                                {rules.summary || `${selectedEvent.prize_pool} VTTN 상금 이벤트`}
                              </div>
                              {rules.entry_requirement && (
                                <div className="text-sm text-gray-600 mt-1">• {rules.entry_requirement}</div>
                              )}
                            </div>
                            <div className="bg-white p-3 rounded border border-green-100">
                              <div className="font-medium text-gray-700 mb-1">사용 제한</div>
                              {usageList.length > 0 ? (
                                usageList.map((item, idx) => (
                                  <div key={idx} className="text-sm text-gray-600">• {item}</div>
                                ))
                              ) : (
                                <div className="text-sm text-gray-400">제한 없음</div>
                              )}
                            </div>
                            <div className="bg-white p-3 rounded border border-green-100">
                              <div className="font-medium text-gray-700 mb-1">출금 / 기타</div>
                              {withdrawal.start && (
                                <div className="text-sm text-gray-600">• 출금 시작일: {withdrawal.start}</div>
                              )}
                              {withdrawal.schedule && (
                                <div className="text-sm text-gray-600">• 스케줄: {withdrawal.schedule}</div>
                              )}
                              {notes.length > 0 &&
                                notes.map((item, idx) => (
                                  <div key={idx} className="text-sm text-gray-600">• {item}</div>
                                ))}
                              {!withdrawal.start && !withdrawal.schedule && notes.length === 0 && (
                                <div className="text-sm text-gray-400">추가 정보 없음</div>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* 상금 분배 구조 */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <Trophy className="w-5 h-5 text-yellow-600" />
                      <h3 className="text-lg font-semibold text-yellow-800">🏆 상금 분배 구조</h3>
                    </div>
                    {(() => {
                      const prizePoolAmount = Number(selectedEvent.prize_pool || 0);
                      const distribution = normalizePrizeDistribution(
                        selectedEvent.prize_distribution,
                        prizePoolAmount
                      ).distribution;
                      const tierRanges = buildTierRanges(distribution, prizePoolAmount);
                      
                      return (
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                          {tierRanges.map((range, idx) => {
                            const totalAmount = range.prize * range.count;
                            const percentage = prizePoolAmount > 0 ? (totalAmount / prizePoolAmount) * 100 : 0;
                            
                            return (
                              <div key={idx} className="bg-white border border-yellow-300 rounded-lg p-4 text-center">
                                <div className="text-lg font-bold text-yellow-800 mb-2">{range.label}</div>
                                <div className="space-y-1">
                                  <div className="text-2xl font-bold text-green-600">
                                    {range.prize.toLocaleString()}
                                  </div>
                                  <div className="text-xs text-gray-500">VTTN / 명</div>
                                  <div className="text-sm text-gray-700 font-medium">× {range.count}명</div>
                                  <div className="border-t border-yellow-200 mt-2 pt-2">
                                    <div className="text-lg font-semibold text-orange-600">
                                      {totalAmount.toLocaleString()}
                                    </div>
                                    <div className="text-xs text-gray-500">총 VTTN</div>
                                    <div className="text-xs text-yellow-700 mt-1">
                                      ({percentage.toFixed(1)}%)
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {managementTab === 'participants' && (
                <div className="space-y-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <h4 className="text-base font-semibold text-gray-800">전체 참가자</h4>
                    <div className="relative w-full sm:w-64">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={participantTabSearch}
                        onChange={(e) => setParticipantTabSearch(e.target.value)}
                        placeholder="닉네임·추천코드·지갑 검색"
                        className="w-full pl-10 pr-8 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-500 text-sm bg-white"
                      />
                      {participantTabSearch && (
                        <button
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-gray-700"
                          onClick={() => setParticipantTabSearch('')}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>

                  {/* 통계 카드 */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="bg-white p-4 rounded border text-center">
                      <div className="font-bold text-2xl text-blue-600">{participantsTotal || participants.length}</div>
                      <div className="text-sm text-gray-600">총 참여자</div>
                    </div>
                    <div className="bg-white p-4 rounded border text-center">
                      <div className="font-bold text-2xl text-green-600">
                        {selectedEvent?.prize_pool ? Number(selectedEvent.prize_pool).toLocaleString() : '0'}
                      </div>
                      <div className="text-sm text-gray-600">총상금 VTTN</div>
                    </div>
                    <div className="bg-white p-4 rounded border text-center">
                      <div className="font-bold text-2xl text-orange-500">
                        {participants.filter(p => p.status === 'winner').length}
                      </div>
                      <div className="text-sm text-gray-600">당첨자</div>
                    </div>
                    <div className="bg-white p-4 rounded border text-center">
                      <div className="font-bold text-2xl text-purple-500">
                        {participants.filter(p => p.status === 'on_hold').length}
                      </div>
                      <div className="text-sm text-gray-600">보류</div>
                    </div>
                    <div className="bg-white p-4 rounded border text-center">
                      <div className="font-bold text-2xl text-gray-600">
                        {participants.filter(p => p.status === 'eliminated' || p.status === 'removed').length}
                      </div>
                      <div className="text-sm text-gray-600">탈락</div>
                    </div>
                  </div>

                  {/* 참가자 테이블 */}
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="border-b border-gray-200 px-4 py-3 text-left text-sm font-semibold">번호</th>
                            <th className="border-b border-gray-200 px-4 py-3 text-left text-sm font-semibold">닉네임</th>
                            <th className="border-b border-gray-200 px-4 py-3 text-left text-sm font-semibold">추천코드</th>
                            <th className="border-b border-gray-200 px-4 py-3 text-left text-sm font-semibold">지갑 주소</th>
                            <th className="border-b border-gray-200 px-4 py-3 text-left text-sm font-semibold">참가비</th>
                            <th className="border-b border-gray-200 px-4 py-3 text-left text-sm font-semibold">상태</th>
                            <th className="border-b border-gray-200 px-4 py-3 text-left text-sm font-semibold">등수</th>
                            <th className="border-b border-gray-200 px-4 py-3 text-left text-sm font-semibold">상금</th>
                            <th className="border-b border-gray-200 px-4 py-3 text-left text-sm font-semibold">참여일시</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredParticipants
                            .slice()
                            .sort((a, b) => new Date(b.joined_at).getTime() - new Date(a.joined_at).getTime())
                            .map((participant, idx) => {
                              const normalizedStatus =
                                participant.status === 'removed' ? 'eliminated' : participant.status;
                              return (
                                <tr
                                  key={participant.id}
                                  className={`hover:bg-gray-50 ${
                                    normalizedStatus === 'winner'
                                      ? 'bg-yellow-50'
                                      : normalizedStatus === 'on_hold'
                                        ? 'bg-purple-50'
                                        : normalizedStatus === 'eliminated'
                                          ? 'bg-red-50'
                                          : ''
                                  }`}
                                >
                                  <td className="border-b border-gray-200 px-4 py-3 text-sm text-gray-500">
                                    {idx + 1}
                                  </td>
                                  <td className="border-b border-gray-200 px-4 py-3 text-sm font-medium">
                                    {participant.nickname}
                                  </td>
                                  <td className="border-b border-gray-200 px-4 py-3 text-sm text-gray-700">
                                    {participant.referral_code || '-'}
                                  </td>
                                  <td className="border-b border-gray-200 px-4 py-3 font-mono text-sm">
                                    {participant.wallet_address.slice(0, 8)}...{participant.wallet_address.slice(-6)}
                                  </td>
                                  <td className="border-b border-gray-200 px-4 py-3 text-sm text-gray-600">
                                    {participant.tokens_used} VTTN
                                  </td>
                                  <td className="border-b border-gray-200 px-4 py-3">
                                    <span className={`inline-block px-2 py-1 text-xs font-medium rounded ${
                                      normalizedStatus === 'winner'
                                        ? 'bg-yellow-100 text-yellow-800'
                                        : normalizedStatus === 'active'
                                          ? 'bg-green-100 text-green-800'
                                          : normalizedStatus === 'on_hold'
                                            ? 'bg-purple-100 text-purple-800'
                                            : 'bg-red-100 text-red-800'
                                    }`}>
                                      {normalizedStatus === 'winner'
                                        ? '당첨'
                                        : normalizedStatus === 'active'
                                          ? '참여'
                                          : normalizedStatus === 'on_hold'
                                            ? '보류'
                                            : '탈락'}
                                    </span>
                                  </td>
                                  <td className="border-b border-gray-200 px-4 py-3 text-sm font-semibold">
                                    {participant.manual_rank ? (
                                      <span className="text-orange-600">
                                        {formatTierLabel(participant.manual_rank, selectedEvent?.id)}
                                      </span>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </td>
                                  <td className="border-b border-gray-200 px-4 py-3 text-sm font-semibold">
                                    {participant.reward_received ? (
                                      <span className="text-green-600">
                                        {Number(participant.reward_received).toLocaleString()} VTTN
                                      </span>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </td>
                                  <td className="border-b border-gray-200 px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                                    {new Date(participant.joined_at).toLocaleString('ko-KR', { 
                                      year: '2-digit', 
                                      month: '2-digit', 
                                      day: '2-digit', 
                                      hour: '2-digit', 
                                      minute: '2-digit' 
                                    })}
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {managementTab === 'winners' && (
                <div className="space-y-6">
                  {/* 상단 액션 버튼 + 검색 */}
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center p-4 bg-orange-50 border-2 border-orange-200 rounded-lg">
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={handleRandomAssignWinners}
                        className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-semibold shadow-md"
                      >
                        랜덤 선정
                      </button>
                      <button
                        onClick={handleBulkSave}
                        disabled={!hasParticipantChanges}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold shadow-md transition-colors ${
                          hasParticipantChanges
                            ? 'bg-green-500 text-white hover:bg-green-600'
                            : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        전체 저장
                      </button>
                      <button
                        onClick={handleResetWinners}
                        className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm font-semibold"
                      >
                        초기화
                      </button>
                    </div>

                    <div className="w-full lg:w-72 lg:ml-auto">
                      <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          value={winnerSearchTerm}
                          onChange={(e) => setWinnerSearchTerm(e.target.value)}
                          placeholder="닉네임·추천코드·지갑 검색"
                          className="w-full pl-10 pr-8 py-2 border border-orange-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-500 text-sm bg-white"
                        />
                        {winnerSearchTerm && (
                          <button
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-gray-700"
                            onClick={() => setWinnerSearchTerm('')}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* 등수별 배정 현황 (요약) */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-3">📊 등수별 배정 현황</h4>
                    <div className="grid grid-cols-5 gap-2">
                      {selectedEvent && (() => {
                        const prizePoolAmount = Number(selectedEvent.prize_pool || 0);
                        const distribution = normalizePrizeDistribution(
                          selectedEvent.prize_distribution,
                          prizePoolAmount
                        ).distribution;
                        const tierRanges = buildTierRanges(distribution, prizePoolAmount);
                        
                        return tierRanges.map((range, idx) => {
                          const assignedCount = participants.filter(p => 
                            p.status === 'winner' && 
                            p.manual_rank !== null && 
                            p.manual_rank !== undefined &&
                            p.manual_rank >= range.start && 
                            p.manual_rank <= range.end
                          ).length;
                          const isComplete = assignedCount === range.count;
                          
                          return (
                            <div key={idx} className="flex flex-col items-center justify-center gap-1 px-3 py-2 bg-white rounded-lg border border-gray-200 text-xs">
                              <span className="font-bold text-gray-800">{range.label}</span>
                              <div className="flex items-center gap-1">
                                <span className="text-gray-600">{range.prize.toLocaleString()} VTTN</span>
                                <span className="text-gray-500">×{range.count}명</span>
                                <span className={`font-medium ${isComplete ? 'text-green-600' : 'text-orange-500'}`}>
                                  ({assignedCount}/{range.count})
                                </span>
                                {isComplete && <span className="text-green-500">✅</span>}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>

                  {/* 등수별 섹션 */}
                  {selectedEvent && (() => {
                    const prizePoolAmount = Number(selectedEvent.prize_pool || 0);
                    const distribution = normalizePrizeDistribution(
                      selectedEvent.prize_distribution,
                      prizePoolAmount
                    ).distribution;
                    const tierRanges = buildTierRanges(distribution, prizePoolAmount);
                    
                    return tierRanges.map((range, idx) => {
                    // winners state에서 필터링 (데이터베이스에서 로드한 당첨자)
                    const winnersForRange = winners.filter(w => 
                        w.rank >= range.start && 
                        w.rank <= range.end
                    );

                    const winnersInRank = winnersForRange
                      .slice()
                      .sort((a, b) => (a.rank || 0) - (b.rank || 0))
                      .filter((w) => {
                        const term = winnerSearchTerm.trim().toLowerCase();
                        if (!term) return true;
                        const nickname = (w.nickname || '').toLowerCase();
                        const referral = (w.referral_code || '').toLowerCase();
                        const wallet = (w.wallet_address || '').toLowerCase();
                        return nickname.includes(term) || referral.includes(term) || wallet.includes(term);
                      });

                    const totalAssignedCount = winnersForRange.length;

                    return (
                        <div key={idx} className="border-2 border-gray-300 rounded-lg p-4 bg-white">
                          {/* 등수별 헤더 */}
                          <div className="flex justify-between items-center mb-4 bg-gradient-to-r from-yellow-50 to-orange-50 p-4 rounded-lg border-2 border-yellow-200">
                            <div>
                              <h4 className="text-xl font-bold text-yellow-800 mb-1">{range.label}</h4>
                              <p className="text-sm text-gray-700">
                                상금: <span className="font-bold text-green-600">{range.prize.toLocaleString()} VTTN</span> × {range.count}명 
                                = 총 <span className="font-bold text-orange-600">{(range.prize * range.count).toLocaleString()} VTTN</span>
                              </p>
                              <p className="text-xs text-gray-600 mt-1">
                                현재 배정: <span className={`font-bold ${totalAssignedCount === range.count ? 'text-green-600' : 'text-orange-500'}`}>
                                  {totalAssignedCount}/{range.count}명
                                </span>
                                {selectedEvent?.id === 2 && range.start === 5 && totalAssignedCount < range.count && (
                                  <span className="text-xs text-blue-600 ml-2">
                                    (1등~4등 먼저 채우기)
                                  </span>
                                )}
                              </p>
                            </div>
                            
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  setSelectedRankForAdd(range.start);
                                  setShowWinnerSearchModal(true);
                                  setEditingWinnerId(null);
                                }}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
                              >
                                ➕ 당첨자 추가
                              </button>
                              <button
                                onClick={() => {
                                  // ⭐ 2차 이벤트의 5등: 1등~4등까지 다 채운 후 남은 인원만 선정
                                  const is2ndEvent = selectedEvent?.id === 2;
                                  const is5thRank = range.start === 5;
                                  
                                  let actualNeededCount = range.count - totalAssignedCount;
                                  
                                  if (is2ndEvent && is5thRank) {
                                    // 1등~4등까지 필요한 총 인원: 1 + 5 + 30 + 100 = 136명
                                    const requiredBefore5th = 1 + 5 + 30 + 100;
                                    // 현재 1등~4등까지 배정된 인원 계산
                                    const assignedBefore5th = participants.filter(p => 
                                      p.status === 'winner' && 
                                      p.manual_rank !== null && 
                                      p.manual_rank !== undefined &&
                                      p.manual_rank >= 1 && 
                                      p.manual_rank <= 136
                                    ).length;
                                    // 남은 참가자 수
                                    const availableFor5th = participants.filter(p => p.status !== 'winner').length;
                                    // 5등은 최대 200명이지만, 실제로는 남은 인원만큼만 선정
                                    actualNeededCount = Math.min(range.count - totalAssignedCount, availableFor5th);
                                    
                                    if (assignedBefore5th < requiredBefore5th) {
                                      alert(`⚠️ 1등~4등까지 먼저 다 채워주세요!\n\n현재 1등~4등 배정: ${assignedBefore5th}/${requiredBefore5th}명\n\n1등부터 4등까지 먼저 채운 후 5등을 선정하세요.`);
                                      return;
                                    }
                                  }
                                  
                                  if (!window.confirm(`${range.label} 당첨자를 랜덤으로 선정하시겠습니까?\n\n현재 배정: ${winnersInRank.length}/${range.count}명${is2ndEvent && is5thRank ? `\n실제 선정: ${actualNeededCount}명` : ''}`)) return;
                                  
                                  try {
                                    // 이미 당첨자가 아닌 참가자 목록
                                    const availableParticipants = participants.filter(p => p.status !== 'winner');
                                    
                                    if (availableParticipants.length === 0) {
                                      alert('선정 가능한 참가자가 없습니다.');
                                      return;
                                    }
                                    
                                    // 필요한 인원수 계산
                                    const neededCount = is2ndEvent && is5thRank ? actualNeededCount : (range.count - totalAssignedCount);
                                    
                                    if (neededCount <= 0) {
                                      alert('이미 모든 인원이 배정되었습니다.');
                                      return;
                                    }
                                    
                                    if (availableParticipants.length < neededCount) {
                                      alert(`선정 가능한 참가자가 부족합니다. (필요: ${neededCount}명, 가능: ${availableParticipants.length}명)`);
                                      return;
                                    }
                                    
                                    // Fisher-Yates 셔플 알고리즘으로 랜덤 선정
                                    const shuffled = [...availableParticipants].sort(() => Math.random() - 0.5);
                                    const selected = shuffled.slice(0, neededCount);
                                    
                                    // ⭐ 중요: 등수별 섹션에서는 모두 같은 등수를 배정!
                                    // 예: 1등 섹션 → 모두 1등, 2등 섹션 → 모두 2등
                                    const assignedRank = range.start; // 해당 섹션의 시작 등수 = 대표 등수
                                    
                                    // 참가자 업데이트 (모두 동일한 등수로 배정)
                                    const updatedParticipants = participants.map(p => {
                                      const selectedIndex = selected.findIndex(s => s.id === p.id);
                                      if (selectedIndex >= 0) {
                                        return { ...p, status: 'winner' as const, manual_rank: assignedRank };
                                      }
                                      return p;
                                    });
                                    
                                    setParticipants(updatedParticipants);
                                    alert(`${range.label} 당첨자 ${neededCount}명을 랜덤으로 선정했습니다.${is2ndEvent && is5thRank ? `\n\n(계획: ${range.count}명, 실제: ${neededCount}명)` : ''}`);
                                  } catch (error) {
                                    console.error('랜덤 선정 오류:', error);
                                    alert('랜덤 선정 중 오류가 발생했습니다.');
                                  }
                                }}
                                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium"
                              >
                                🎲 {range.label} 선정
                              </button>
                              <button
                                onClick={async () => {
                                  if (!window.confirm(`${range.label} 당첨자 ${winnersInRank.length}명을 저장하시겠습니까?`)) return;
                                  
                                  try {
                                    if (!selectedEvent) return;
                                    
                                    const response = await fetch(
                                      buildApiUrl(`/api/admin/boom-events/${selectedEvent.id}/winners/rank-save`),
                                      {
                                        method: 'PUT',
                                        headers: {
                                          'Content-Type': 'application/json'
                                        },
                                        body: JSON.stringify({
                                          winners: winnersInRank.map(w => ({
                                            participant_id: w.participant_id || w.id,
                                            user_address: w.wallet_address,
                                            rank: w.rank,
                                            prize_amount: range.prize
                                          }))
                                        })
                                      }
                                    );
                                    
                                    if (response.ok) {
                                      alert(`${range.label} 당첨자 ${winnersInRank.length}명을 저장했습니다.`);
                                      loadParticipants(selectedEvent.id);
                                    } else {
                                      const data = await response.json().catch(() => ({}));
                                      alert(`저장 실패: ${data.error || response.statusText}`);
                                    }
                                  } catch (error) {
                                    console.error('저장 오류:', error);
                                    alert('저장 중 오류가 발생했습니다.');
                                  }
                                }}
                                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-medium"
                              >
                                💾 {range.label} 저장
                              </button>
                            </div>
                          </div>

                          {/* 등수별 당첨자 테이블 */}
                          <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                              <thead>
                                <tr className="bg-gray-50">
                                  <th className="border-b border-gray-200 px-4 py-2 text-left text-sm font-semibold w-24">등수</th>
                                  <th className="border-b border-gray-200 px-4 py-2 text-left text-sm font-semibold">닉네임</th>
                                  <th className="border-b border-gray-200 px-4 py-2 text-left text-sm font-semibold">추천코드</th>
                                  <th className="border-b border-gray-200 px-4 py-2 text-left text-sm font-semibold">지갑 주소</th>
                                  <th className="border-b border-gray-200 px-4 py-2 text-left text-sm font-semibold">참가비</th>
                                  <th className="border-b border-gray-200 px-4 py-2 text-left text-sm font-semibold">상금</th>
                                  <th className="border-b border-gray-200 px-4 py-2 text-left text-sm font-semibold">지급상태</th>
                                  <th className="border-b border-gray-200 px-4 py-2 text-center text-sm font-semibold w-48">관리</th>
                                </tr>
                              </thead>
                              <tbody>
                                {winnersInRank.length > 0 ? (
                                  winnersInRank.map((winner) => (
                                    <tr key={winner.id} className="hover:bg-yellow-50">
                                      <td className="border-b border-gray-200 px-4 py-2 text-sm font-bold text-orange-600">
                                        {range.label}
                                      </td>
                                      <td className="border-b border-gray-200 px-4 py-2 text-sm font-medium">
                                        {winner.nickname || '-'}
                                      </td>
                                      <td className="border-b border-gray-200 px-4 py-2 text-sm text-gray-700">
                                        {winner.referral_code || '-'}
                                      </td>
                                      <td className="border-b border-gray-200 px-4 py-2 font-mono text-sm">
                                        {winner.wallet_address && winner.wallet_address.length > 0 
                                          ? `${winner.wallet_address.slice(0, 8)}...${winner.wallet_address.slice(-6)}`
                                          : (winner as any).user_address && (winner as any).user_address.length > 0
                                            ? `${(winner as any).user_address.slice(0, 8)}...${(winner as any).user_address.slice(-6)}`
                                            : '-'}
                                      </td>
                                      <td className="border-b border-gray-200 px-4 py-2 text-sm text-gray-600">
                                        {(winner as any).tokens_used ? Number((winner as any).tokens_used).toLocaleString() : selectedEvent.participation_fee} VTTN
                                      </td>
                                      <td className="border-b border-gray-200 px-4 py-2 text-sm font-bold text-green-600">
                                        {Number(winner.prize_amount || 0).toLocaleString()} VTTN
                                      </td>
                                      <td className="border-b border-gray-200 px-4 py-2">
                                        {winner.payout_status === 'completed' || winner.payout_status === 'paid' ? (
                                          <span className="inline-block px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800">
                                            ✅ 지급완료
                                          </span>
                                        ) : (
                                          <span className="inline-block px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-600">
                                            ⏳ 미지급
                                          </span>
                                        )}
                                      </td>
                                      <td className="border-b border-gray-200 px-4 py-2">
                                        <div className="flex gap-1 justify-center">
                                          <button
                                            onClick={async () => {
                                              if (window.confirm(`${winner.nickname}님의 당첨 정보를 저장하시겠습니까?`)) {
                                                // 개별 저장 로직 (임시)
                                                alert('개별 저장 기능은 곧 구현됩니다.');
                                              }
                                            }}
                                            className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                                            title="저장"
                                          >
                                            저장
                                          </button>
                                          <button
                                            onClick={() => {
                                              if (window.confirm(`${winner.nickname}님을 보류 상태로 변경하시겠습니까?`)) {
                                                handleRemoveWinner(winner.id);
                                              }
                                            }}
                                            className="px-2 py-1 text-xs bg-purple-500 text-white rounded hover:bg-purple-600"
                                            title="보류"
                                          >
                                            보류
                                          </button>
                                          <button
                                            onClick={async () => {
                                              if (window.confirm(`${winner.nickname}님을 당첨자에서 완전히 제외하시겠습니까?`)) {
                                                try {
                                                  const updatedParticipants = participants.map(p => {
                                                    if (p.id === winner.id) {
                                                      return { ...p, status: 'eliminated' as const, manual_rank: null };
                                                    }
                                                    return p;
                                                  });
                                                  
                                                  setParticipants(updatedParticipants);
                                                  alert('당첨자를 제외(탈락) 처리했습니다.');
                                                } catch (error) {
                                                  console.error('당첨자 삭제 오류:', error);
                                                  alert('당첨자 삭제 중 오류가 발생했습니다.');
                                                }
                                              }
                                            }}
                                            className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                                            title="삭제"
                                          >
                                            삭제
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan={8} className="border-b border-gray-200 px-4 py-8 text-center text-gray-500">
                                      <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                                      <p>아직 배정된 당첨자가 없습니다.</p>
                                      <p className="text-sm mt-1">상단의 "🎲 {range.label} 선정" 버튼을 클릭하세요.</p>
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}

              {managementTab === 'payment' && (
                <div className="space-y-6">
                  {/* 상단 액션 버튼 + 검색 */}
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => {
                          alert('전체 일시 지급 기능은 Phase 3에서 구현됩니다.');
                        }}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-semibold shadow-md"
                      >
                        전체 지급
                      </button>
                      <button
                        onClick={() => {
                          alert('등급별 지급 기능은 Phase 3에서 구현됩니다.');
                        }}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-semibold"
                      >
                        등급별 지급
                      </button>
                      <button
                        onClick={() => {
                          alert('개수별 지급 기능은 Phase 3에서 구현됩니다.');
                        }}
                        className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-sm font-semibold"
                      >
                        개수별 지급
                      </button>
                    </div>

                    <div className="w-full lg:w-72 lg:ml-auto">
                      <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          value={winnerSearchTerm}
                          onChange={(e) => setWinnerSearchTerm(e.target.value)}
                          placeholder="닉네임·추천코드·지갑 검색"
                          className="w-full pl-10 pr-8 py-2 border border-green-200 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-green-500 text-sm bg-white"
                        />
                        {winnerSearchTerm && (
                          <button
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-gray-700"
                            onClick={() => setWinnerSearchTerm('')}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* 지급 현황 요약 */}
                  <div className="grid grid-cols-4 gap-3">
                    <div className="bg-white p-4 rounded border text-center">
                      <div className="font-bold text-2xl text-blue-600">
                        {participants.filter(p => p.status === 'winner').length}
                      </div>
                      <div className="text-sm text-gray-600">총 당첨자</div>
                    </div>
                    <div className="bg-white p-4 rounded border text-center">
                      <div className="font-bold text-2xl text-green-600">
                        {participants.filter(p => p.status === 'winner' && p.reward_received && Number(p.reward_received) > 0).length}
                      </div>
                      <div className="text-sm text-gray-600">지급 완료</div>
                    </div>
                    <div className="bg-white p-4 rounded border text-center">
                      <div className="font-bold text-2xl text-orange-500">
                        {participants.filter(p => p.status === 'winner' && (!p.reward_received || Number(p.reward_received) === 0)).length}
                      </div>
                      <div className="text-sm text-gray-600">지급 대기</div>
                    </div>
                    <div className="bg-white p-4 rounded border text-center">
                      <div className="font-bold text-2xl text-green-600">
                        {participants
                          .filter(p => p.status === 'winner' && p.reward_received)
                          .reduce((sum, p) => sum + Number(p.reward_received || 0), 0)
                          .toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-600">총 지급액 (VTTN)</div>
                    </div>
                  </div>

                  {/* 당첨자 지급 테이블 (등수별 정렬) */}
                  {selectedEvent && (() => {
                    const prizePoolAmount = Number(selectedEvent.prize_pool || 0);
                    const distribution = normalizePrizeDistribution(
                      selectedEvent.prize_distribution,
                      prizePoolAmount
                    ).distribution;
                    const tierRanges = buildTierRanges(distribution, prizePoolAmount);
                    
                    return tierRanges.map((range, idx) => {
                      // winners state에서 필터링 (데이터베이스에서 로드한 당첨자)
                      const winnersForRange = winners.filter(w => 
                        w.rank >= range.start && 
                        w.rank <= range.end
                      );

                      const winnersInRank = winnersForRange
                        .slice()
                        .sort((a, b) => (a.rank || 0) - (b.rank || 0))
                        .filter((w) => {
                          const term = winnerSearchTerm.trim().toLowerCase();
                          if (!term) return true;
                          const nickname = (w.nickname || '').toLowerCase();
                          const referral = (w.referral_code || '').toLowerCase();
                          const wallet = (w.wallet_address || '').toLowerCase();
                          return nickname.includes(term) || referral.includes(term) || wallet.includes(term);
                        });

                      if (winnersForRange.length === 0) return null;

                      return (
                        <div key={idx} className="border-2 border-gray-300 rounded-lg p-4 bg-white">
                          {/* 등수별 헤더 */}
                          <div className="flex justify-between items-center mb-4 bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg border-2 border-green-200">
                            <div>
                              <h4 className="text-xl font-bold text-green-800 mb-1">{range.label} 당첨금 지급</h4>
                              <p className="text-sm text-gray-700">
                                개인당 상금: <span className="font-bold text-green-600">{range.prize.toLocaleString()} VTTN</span> × {winnersForRange.length}명 
                                = 총 <span className="font-bold text-orange-600">{(range.prize * winnersForRange.length).toLocaleString()} VTTN</span>
                              </p>
                            </div>
                            
                            <button
                              onClick={() => {
                                alert(`${range.label} 일괄 지급 기능은 Phase 3에서 구현됩니다.`);
                              }}
                              className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-semibold shadow-md"
                            >
                              💰 {range.label} 일괄 지급
                            </button>
                          </div>

                          {/* 등수별 당첨자 지급 테이블 */}
                          <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                              <thead>
                                <tr className="bg-gray-50">
                                  <th className="border-b border-gray-200 px-4 py-2 text-left text-sm font-semibold w-20">등수</th>
                                  <th className="border-b border-gray-200 px-4 py-2 text-left text-sm font-semibold">닉네임</th>
                                  <th className="border-b border-gray-200 px-4 py-2 text-left text-sm font-semibold">추천코드</th>
                                  <th className="border-b border-gray-200 px-4 py-2 text-left text-sm font-semibold">지갑 주소</th>
                                  <th className="border-b border-gray-200 px-4 py-2 text-left text-sm font-semibold">상금</th>
                                  <th className="border-b border-gray-200 px-4 py-2 text-left text-sm font-semibold">지급 상태</th>
                                  <th className="border-b border-gray-200 px-4 py-2 text-left text-sm font-semibold">현재 잔고</th>
                                  <th className="border-b border-gray-200 px-4 py-2 text-center text-sm font-semibold">액션</th>
                                </tr>
                              </thead>
                              <tbody>
                                {winnersInRank.map((winner) => (
                                  <tr key={winner.id} className="hover:bg-green-50">
                                    <td className="border-b border-gray-200 px-4 py-2 text-sm font-bold text-orange-600">
                                      {formatTierLabel(winner.rank, selectedEvent?.id)}
                                    </td>
                                    <td className="border-b border-gray-200 px-4 py-2 text-sm font-medium">
                                      {winner.nickname || '-'}
                                    </td>
                                    <td className="border-b border-gray-200 px-4 py-2 text-sm text-gray-700">
                                      {winner.referral_code || '-'}
                                    </td>
                                    <td className="border-b border-gray-200 px-4 py-2 font-mono text-sm">
                                      {winner.wallet_address?.slice(0, 8)}...{winner.wallet_address?.slice(-6)}
                                    </td>
                                    <td className="border-b border-gray-200 px-4 py-2 text-sm font-bold text-green-600">
                                      {Number(winner.prize_amount || 0).toLocaleString()} VTTN
                                    </td>
                                    <td className="border-b border-gray-200 px-4 py-2">
                                      {winner.payout_status === 'completed' || winner.payout_status === 'paid' ? (
                                        <span className="inline-block px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800">
                                          ✅ 지급완료
                                        </span>
                                      ) : (
                                        <span className="inline-block px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-600">
                                          ⏳ 미지급
                                        </span>
                                      )}
                                    </td>
                                    <td className="border-b border-gray-200 px-4 py-2 text-sm text-gray-600">
                                      <span className="text-gray-400">-</span>
                                    </td>
                                    <td className="border-b border-gray-200 px-4 py-2 text-center">
                                      <button
                                        onClick={() => {
                                          alert('개별 지급 기능은 Phase 3에서 구현됩니다.');
                                        }}
                                        disabled={!!((winner as any).reward_received && Number((winner as any).reward_received) > 0)}
                                        className={`px-3 py-1 text-xs rounded font-medium ${
                                          (winner as any).reward_received && Number((winner as any).reward_received) > 0
                                            ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                            : 'bg-green-500 text-white hover:bg-green-600'
                                        }`}
                                      >
                                        💰 지급
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}

              {managementTab === 'stats' && (
                <div className="space-y-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">📈 전체 이벤트 통계</h3>
                  
                  {/* 전체 통계 요약 */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-lg text-white shadow-lg">
                      <div className="text-3xl font-bold mb-2">
                        {selectedEvent?.participants_count || participants.length}
                      </div>
                      <div className="text-sm opacity-90">총 참여자 수</div>
                    </div>
                    <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-lg text-white shadow-lg">
                      <div className="text-3xl font-bold mb-2">
                        {participants.filter(p => p.status === 'winner').length}
                      </div>
                      <div className="text-sm opacity-90">총 당첨자 수</div>
                    </div>
                    <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-6 rounded-lg text-white shadow-lg">
                      <div className="text-3xl font-bold mb-2">
                        {selectedEvent?.prize_pool ? Number(selectedEvent.prize_pool).toLocaleString() : '0'}
                      </div>
                      <div className="text-sm opacity-90">총 상금 (VTTN)</div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-6 rounded-lg text-white shadow-lg">
                      <div className="text-3xl font-bold mb-2">
                        {participants
                          .filter(p => p.status === 'winner' && p.reward_received)
                          .reduce((sum, p) => sum + Number(p.reward_received || 0), 0)
                          .toLocaleString()}
                      </div>
                      <div className="text-sm opacity-90">누적 지급액 (VTTN)</div>
                    </div>
                  </div>

                  {/* 등수별 통계 */}
                  {selectedEvent && (() => {
                    const prizePoolAmount = Number(selectedEvent.prize_pool || 0);
                    const distribution = normalizePrizeDistribution(
                      selectedEvent.prize_distribution,
                      prizePoolAmount
                    ).distribution;
                    const tierRanges = buildTierRanges(distribution, prizePoolAmount);
                    
                    return (
                      <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">🏆 등수별 통계</h4>
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                          {tierRanges.map((range, idx) => {
                            const winnersInRank = participants.filter(p => 
                              p.status === 'winner' && 
                              p.manual_rank !== null && 
                              p.manual_rank !== undefined &&
                              p.manual_rank >= range.start && 
                              p.manual_rank <= range.end
                            );
                            const paidCount = winnersInRank.filter(w => w.payout_status === 'completed' || w.payout_status === 'paid').length;
                            const totalPaid = winnersInRank
                              .filter(w => w.reward_received)
                              .reduce((sum, w) => sum + Number(w.reward_received || 0), 0);

                            return (
                              <div key={idx} className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-200 rounded-lg p-4">
                                <div className="text-center mb-3">
                                  <div className="text-lg font-bold text-yellow-800">{range.label}</div>
                                  <div className="text-sm text-gray-600">({range.start}~{range.end}등)</div>
                                </div>
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">당첨자:</span>
                                    <span className="font-bold text-blue-600">{winnersInRank.length}명</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">지급완료:</span>
                                    <span className="font-bold text-green-600">{paidCount}명</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">개인상금:</span>
                                    <span className="font-bold text-orange-600">{range.prize.toLocaleString()}</span>
                                  </div>
                                  <div className="border-t border-yellow-200 pt-2 mt-2">
                                    <div className="flex justify-between">
                                      <span className="text-gray-700 font-medium">총 지급:</span>
                                      <span className="font-bold text-purple-600">{totalPaid.toLocaleString()}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Top 당첨자 (당첨금 순) */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">💰 Top 당첨자 (당첨금 순)</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="border-b border-gray-200 px-4 py-3 text-left text-sm font-semibold">순위</th>
                            <th className="border-b border-gray-200 px-4 py-3 text-left text-sm font-semibold">등수</th>
                            <th className="border-b border-gray-200 px-4 py-3 text-left text-sm font-semibold">닉네임</th>
                            <th className="border-b border-gray-200 px-4 py-3 text-left text-sm font-semibold">추천코드</th>
                            <th className="border-b border-gray-200 px-4 py-3 text-left text-sm font-semibold">지갑 주소</th>
                            <th className="border-b border-gray-200 px-4 py-3 text-left text-sm font-semibold">당첨금 (VTTN)</th>
                            <th className="border-b border-gray-200 px-4 py-3 text-left text-sm font-semibold">지급 상태</th>
                          </tr>
                        </thead>
                        <tbody>
                          {participants
                            .filter(p => p.status === 'winner' && p.reward_received && Number(p.reward_received) > 0)
                            .sort((a, b) => Number(b.reward_received || 0) - Number(a.reward_received || 0))
                            .slice(0, 100)
                            .map((winner, idx) => (
                              <tr key={winner.id} className={`hover:bg-yellow-50 ${idx < 3 ? 'bg-yellow-50' : ''}`}>
                                <td className="border-b border-gray-200 px-4 py-3 text-sm">
                                  {idx === 0 && <span className="text-2xl">🥇</span>}
                                  {idx === 1 && <span className="text-2xl">🥈</span>}
                                  {idx === 2 && <span className="text-2xl">🥉</span>}
                                  {idx >= 3 && <span className="font-medium text-gray-600">{idx + 1}</span>}
                                </td>
                                <td className="border-b border-gray-200 px-4 py-3 text-sm font-bold text-orange-600">
                                  {formatTierLabel(winner.manual_rank, selectedEvent?.id)}
                                </td>
                                <td className="border-b border-gray-200 px-4 py-3 text-sm font-medium">
                                  {winner.nickname}
                                </td>
                                <td className="border-b border-gray-200 px-4 py-3 text-sm text-gray-700">
                                  {winner.referral_code || '-'}
                                </td>
                                <td className="border-b border-gray-200 px-4 py-3 font-mono text-sm">
                                  {winner.wallet_address.slice(0, 8)}...{winner.wallet_address.slice(-6)}
                                </td>
                                <td className="border-b border-gray-200 px-4 py-3 text-sm font-bold text-green-600">
                                  {Number(winner.reward_received).toLocaleString()}
                                </td>
                                <td className="border-b border-gray-200 px-4 py-3">
                                  {winner.payout_status === 'completed' || winner.payout_status === 'paid' ? (
                                    <span className="inline-block px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800">
                                      ✅ 지급완료
                                    </span>
                                  ) : (
                                    <span className="inline-block px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-600">
                                      ⏳ 미지급
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          {participants.filter(p => p.status === 'winner' && p.reward_received && Number(p.reward_received) > 0).length === 0 && (
                            <tr>
                              <td colSpan={7} className="border-b border-gray-200 px-4 py-8 text-center text-gray-500">
                                <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                                <p>아직 지급된 당첨금이 없습니다.</p>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 🆕 당첨자 검색 팝업 */}
      {showWinnerSearchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl mx-4 max-h-[80vh] flex flex-col">
            {/* 헤더 */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {editingWinnerId ? '🔄 당첨자 변경' : '➕ 당첨자 추가'} {selectedRankForAdd && `(${selectedRankForAdd}등)`}
              </h2>
              <button
                onClick={() => {
                  setShowWinnerSearchModal(false);
                  setWinnerSearchQuery('');
                  setWinnerSearchResults([]);
                  setEditingWinnerId(null);
                  setSelectedRankForAdd(null);
                }}
                className="p-2 rounded-full border border-gray-200 hover:bg-gray-100 transition-colors text-gray-500"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* 검색 입력 */}
            <div className="p-6 border-b border-gray-200">
              <div className="relative">
                <input
                  type="text"
                  value={winnerSearchQuery}
                  onChange={(e) => {
                    setWinnerSearchQuery(e.target.value);
                    searchWinners(e.target.value);
                  }}
                  placeholder="닉네임 또는 추천코드로 검색..."
                  className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  autoFocus
                />
                <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              </div>
              <p className="text-sm text-gray-500 mt-2">
                {winnerSearchQuery.length < 2 ? '최소 2글자 이상 입력하세요.' : `${winnerSearchResults.length}명 검색됨`}
              </p>
            </div>

            {/* 검색 결과 */}
            <div className="flex-1 overflow-y-auto p-6">
              {winnerSearchResults.length > 0 ? (
                <div className="space-y-2">
                  {winnerSearchResults.map((participant) => (
                    <button
                      key={participant.id}
                      onClick={() => {
                        if (editingWinnerId) {
                          handleChangeWinner(editingWinnerId, participant);
                        } else if (selectedRankForAdd) {
                          handleAddWinner(participant, selectedRankForAdd);
                        }
                      }}
                      className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-colors text-left"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <div className="font-semibold text-gray-900">{participant.nickname}</div>
                            {(participant as any).is_participant ? (
                              <span className="inline-block px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800">
                                ✓ 이벤트 참여자
                              </span>
                            ) : (
                              <span className="inline-block px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-600">
                                미참여 (일반 유저)
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            추천코드: {participant.referral_code || '-'}
                          </div>
                          <div className="text-xs text-gray-500 font-mono mt-1">
                            {participant.wallet_address.slice(0, 10)}...{participant.wallet_address.slice(-8)}
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          {(participant as any).is_participant ? (
                            <>
                              <div className="text-sm text-gray-600">
                                참가비: {participant.tokens_used || selectedEvent?.participation_fee || '-'} VTTN
                              </div>
                              <div className="mt-1">
                                <span className={`inline-block px-2 py-1 text-xs font-medium rounded ${
                                  participant.status === 'active' ? 'bg-green-100 text-green-800' :
                                  participant.status === 'on_hold' ? 'bg-purple-100 text-purple-800' :
                                  participant.status === 'eliminated' ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {participant.status === 'active' ? '참여중' :
                                   participant.status === 'on_hold' ? '보류' :
                                   participant.status === 'eliminated' ? '탈락' : participant.status}
                                </span>
                              </div>
                            </>
                          ) : (
                            <div className="text-sm text-gray-500">
                              이벤트 미참여
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : winnerSearchQuery.length >= 2 ? (
                <div className="text-center py-12 text-gray-500">
                  <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p>검색 결과가 없습니다.</p>
                  <p className="text-sm mt-1">다른 키워드로 검색해보세요.</p>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p>닉네임 또는 추천코드를 입력하세요.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BoomEventsPage;